import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import BASE_URL from '../config';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  Spinner,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  Label,
  FormGroup
} from 'reactstrap';
import { User, Mail, Phone, MapPin, Building, Calendar, CreditCard, Package, Receipt, FileText, ShoppingBag, Gift, ChevronDown } from 'lucide-react';
import { getCustomers } from './Api/GmsCustomer';
import { getCustomerMemberships } from './Api/GmsCustomerMembership';
import { getBookings, getCustomerUnbilledServices } from './Api/GmsBooking';
import { getSalesInvoices, getCustomerAdvanceBalance, getCustomerOutstandingCredit, getCustomerBranchLoyaltyBalance } from './Api/GmsBilling';
import { getReceipts, getCustomerCreditInvoices } from './Api/GmsReceipt';
import { getBranches } from './Api/GmsBranch';
import { getEmployees } from './Api/GmsEmployee';
import { getCountries } from './Api/GmsCountry';
import { getSpaServices } from './Api/GmsSpaService';
import { getSpaPackages } from './Api/GmsSpaPackage';
import { getSpaPackageSeries } from './Api/GmsSpaPackageSeries';
import { getBusinesses } from './Api/GmsBusiness';
import { getProducts } from './Api/GmsProduct';
import Select from 'react-select';
import classnames from 'classnames';

const CustomerProfileModal = ({
  isOpen,
  toggle,
  customerId = null,
  customer = null
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [advanceBalance, setAdvanceBalance] = useState(null);
  const [outstandingCredit, setOutstandingCredit] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loyaltyBalance, setLoyaltyBalance] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageSeries, setPackageSeries] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [products, setProducts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState(null);
  
  // WhatsApp Messages State
  const [whatsappMessageCount, setWhatsappMessageCount] = useState(null);
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [whatsappCountLoading, setWhatsappCountLoading] = useState(false);
  const [whatsappMessagesLoading, setWhatsappMessagesLoading] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [timePeriod, setTimePeriod] = useState('1'); // 1 = Last 1 Month
  const [expandedMemberships, setExpandedMemberships] = useState(new Set()); // Track expanded membership rows
  const [unbilledServices, setUnbilledServices] = useState([]); // Unbilled services from bookings
  const [unbilledMemberships, setUnbilledMemberships] = useState([]); // Processed membership installments for unbilled tab
  const [unbilledLoading, setUnbilledLoading] = useState(false);
  const [entityNames, setEntityNames] = useState({}); // Store entity names for memberships
  const [installmentNames, setInstallmentNames] = useState({}); // Store installment names
  const [receipts, setReceipts] = useState([]); // Receipts/payments for payment history
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [creditInvoices, setCreditInvoices] = useState([]); // Credit invoices with correct outstanding amounts
  // Selection state for unbilled items
  const [selectedInstallments, setSelectedInstallments] = useState({}); // {installmentId: {checked: boolean, amount: number}}
  const [selectedUnbilledServices, setSelectedUnbilledServices] = useState({}); // {bookingResourceDetailId: {checked: boolean, amount: number}}
  const [selectAllInstallments, setSelectAllInstallments] = useState(false);
  const [selectAllServices, setSelectAllServices] = useState(false);
  
  // SOAP Notes State
  const [soapNotes, setSoapNotes] = useState([]);
  const [soapNotesLoading, setSoapNotesLoading] = useState(false);
  const [viewSoapModal, setViewSoapModal] = useState(false);
  const [selectedSoap, setSelectedSoap] = useState(null);

  const currencyCode = localStorage.getItem("currencyCode") || "AED";

  // Navigation hook
  const navigate = useNavigate();

  // Notification preferences (UI only for now)
  const [notificationPrefs, setNotificationPrefs] = useState({
    transactional: { sms: true, email: true, whatsapp: false },
    marketing: { sms: true, email: true }
  });

  // Use ref to track if data is already loaded to prevent duplicate calls
  const dataLoadedRef = useRef(false);
  const currentCustomerIdRef = useRef(null);

  useEffect(() => {
    // Only load data when modal is open and we have a customer
    if (isOpen && (customerId || customer)) {
      const currentCustomerId = customer?.customer_Id || customerId;

      // Only load if this is a different customer or data hasn't been loaded yet
      if (currentCustomerIdRef.current !== currentCustomerId || !dataLoadedRef.current) {
        dataLoadedRef.current = false;
        currentCustomerIdRef.current = currentCustomerId;
        loadCustomerData();
      }
    } else if (!isOpen) {
      // Reset flags when modal closes
      dataLoadedRef.current = false;
      currentCustomerIdRef.current = null;
    }
  }, [isOpen, customerId, customer?.customer_Id]);

  // Load unbilled services and membership installments when tab is active
  useEffect(() => {
    if (isOpen && activeTab === 'unbilled' && (customerId || customer?.customer_Id)) {
      loadUnbilledData();
    }
  }, [isOpen, activeTab, customerId, customer]);

  // Reset selections when tab changes or modal closes
  useEffect(() => {
    if (activeTab !== 'unbilled' || !isOpen) {
      setSelectedInstallments({});
      setSelectedUnbilledServices({});
      setSelectAllInstallments(false);
      setSelectAllServices(false);
    }
  }, [activeTab, isOpen]);

  // Sync "Select All" checkboxes with individual selections
  useEffect(() => {
    if (activeTab === 'unbilled' && isOpen) {
      // Check if all installments are selected
      const totalInstallments = unbilledMemberships.reduce((sum, membership) =>
        sum + (membership.unpaidInstallments?.length || 0), 0
      );
      const selectedInstallmentsCount = Object.values(selectedInstallments).filter(item => item.checked).length;
      setSelectAllInstallments(totalInstallments > 0 && selectedInstallmentsCount === totalInstallments);

      // Check if all services are selected
      const totalServices = unbilledServices.length;
      const selectedServicesCount = Object.values(selectedUnbilledServices).filter(item => item.checked).length;
      setSelectAllServices(totalServices > 0 && selectedServicesCount === totalServices);
    }
  }, [activeTab, isOpen, selectedInstallments, selectedUnbilledServices, unbilledMemberships, unbilledServices]);

  // Load payment history when tab is active
  useEffect(() => {
    if (isOpen && activeTab === 'paymentHistory' && (customerId || customer?.customer_Id)) {
      loadPaymentHistory();
    }
  }, [isOpen, activeTab, customerId, customer]);

  // Load WhatsApp count when tab is active
  useEffect(() => {
    if (isOpen && activeTab === 'whatsappMessages' && (customerId || customer?.customer_Id)) {
      fetchWhatsAppCount();
    }
  }, [isOpen, activeTab, customerId, customer]);

  // Load SOAP notes when tab is active
  useEffect(() => {
    if (isOpen && activeTab === 'soapNotes' && (customerId || customer?.customer_Id)) {
      loadSoapNotes(customerId || customer?.customer_Id);
    }
  }, [isOpen, activeTab, customerId, customer]);

  const loadCustomerData = async () => {
    // Prevent duplicate calls
    if (dataLoadedRef.current && currentCustomerIdRef.current === (customer?.customer_Id || customerId)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const passedCustomerId = customer?.customer_Id || customer?.value || customerId;
      let currentCustomer = customer;

      // If customer is partial (missing name or email) or only customerId is provided, fetch it
      if (!customer?.customer_NameEn || !customer?.customer_Email || !customer) {
        if (passedCustomerId) {
          const customers = await getCustomers({ customer_Id: passedCustomerId });
          if (customers && customers.length > 0) {
            currentCustomer = customers[0];
            setCustomerData(currentCustomer);
          } else if (!customer) {
            setError('Customer not found');
            return;
          }
        }
      } else {
        setCustomerData(customer);
      }

      // Load all reference data only when modal is open
      if (isOpen) {
        const [branchesData, employeesData, countriesData, servicesData, packagesData, packageSeriesData, businessesData, productsData] = await Promise.all([
          getBranches().catch(() => []),
          getEmployees().catch(() => []),
          getCountries().catch(() => []),
          getSpaServices().catch(() => []),
          getSpaPackages().catch(() => []),
          getSpaPackageSeries().catch(() => []),
          getBusinesses().catch(() => []),
          getProducts({ status: true }).catch(() => [])
        ]);

        setBranches(branchesData);
        setEmployees(employeesData);
        setCountries(countriesData);
        setServices(servicesData);
        setPackages(packagesData);
        setPackageSeries(packageSeriesData);
        setBusinesses(businessesData);
        setProducts(productsData);
      }

      // Load related data
      const currentCustomerId = customer?.customer_Id || customerId;
      if (currentCustomerId && isOpen) {
        // Load invoices first so we can use businessId from them if needed
        await loadInvoices(currentCustomerId);

        // Then load other data in parallel
        await Promise.all([
          loadMemberships(currentCustomerId),
          loadBookings(currentCustomerId),
          loadFinancialData(currentCustomerId)
        ]);
      }

      // Mark data as loaded
      dataLoadedRef.current = true;
    } catch (err) {
      console.error('Error loading customer data:', err);
      setError('Failed to load customer data');
      dataLoadedRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const loadMemberships = async (customerId) => {
    try {
      const data = await getCustomerMemberships({ customerId });
      setMemberships(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Error loading memberships:', err);
      setMemberships([]);
    }
  };

  const loadBookings = async (customerId) => {
    try {
      const response = await getBookings({ customer_Id: customerId, page: 1, pageSize: 10000 });

      // Handle paginated response
      let data = [];
      if (response && response.bookings) {
        data = response.bookings;
      } else if (Array.isArray(response)) {
        data = response;
      }

      const sortedBookings = Array.isArray(data)
        ? data.sort((a, b) => new Date(b.booking_Date || b.entry_Date) - new Date(a.booking_Date || a.entry_Date))
        : [];
      setAllBookings(sortedBookings);
    } catch (err) {
      console.error('❌ Error loading bookings:', err);
      setAllBookings([]);
    }
  };

  const loadInvoices = async (customerId) => {
    try {
      const data = await getSalesInvoices({ Customer_Id: customerId });
      const sortedInvoices = Array.isArray(data)
        ? data.sort((a, b) => new Date(b.Invoice_Date) - new Date(a.Invoice_Date))
        : [];
      setInvoices(sortedInvoices);
    } catch (err) {
      console.error('❌ Error loading invoices:', err);
      setInvoices([]);
    }
  };

  // Helper functions for membership types
  const normalizeEntityType = (entityType) => {
    if (!entityType) return null;
    const normalized = String(entityType).toUpperCase();
    if (normalized === 'PAC') return 'PAC';
    if (normalized === 'SEP' || normalized === 'SER') return 'SEP';
    return normalized;
  };

  const isPackageType = (entityType) => {
    return normalizeEntityType(entityType) === 'PAC';
  };

  const isPackageSeriesType = (entityType) => {
    return normalizeEntityType(entityType) === 'SEP';
  };

  const loadUnbilledData = async () => {
    const currentCustomerId = customer?.customer_Id || customerId;
    if (!currentCustomerId) return;

    try {
      setUnbilledLoading(true);

      // Fetch both memberships and unbilled services in parallel
      const [membershipsData, unbilledServicesData] = await Promise.all([
        getCustomerMemberships({ customerId: currentCustomerId }),
        getCustomerUnbilledServices({
          CustomerId: currentCustomerId,
          BranchId: null // Always null to get all unbilled services regardless of branch
        }).catch(err => {
          console.error('Error fetching unbilled services:', err);
          return [];
        })
      ]);

      // Process memberships to get unpaid installments
      const processedMemberships = [];
      const entityNamesMap = {};
      const installmentNamesMap = {};

      for (const membership of membershipsData || []) {
        // Check if there are unpaid installments
        const unpaidInstallments = (membership.installments || []).filter(installment => {
          const billedAmount = installment.billedAmount || installment.paidAmount || 0;
          const remainingAmount = Math.round((installment.amount - billedAmount) * 100) / 100;
          return remainingAmount > 0;
        });

        if (unpaidInstallments.length > 0) {
          // Fetch entity name
          try {
            if (isPackageType(membership.entity_Type)) {
              const packageData = await getSpaPackages({ packageId: membership.entity_Id });
              if (packageData && packageData.length > 0) {
                entityNamesMap[membership.entity_Id] = packageData[0].packageName_En || `Package ${membership.entity_Id}`;
              }
            } else if (isPackageSeriesType(membership.entity_Type)) {
              const seriesData = await getSpaPackageSeries({ seriesId: membership.entity_Id });
              if (seriesData && seriesData.length > 0) {
                entityNamesMap[membership.entity_Id] = seriesData[0].seriesName_En || `Series ${membership.entity_Id}`;
                // Fetch installment names
                if (seriesData[0].installments) {
                  seriesData[0].installments.forEach(installment => {
                    installmentNamesMap[installment.installmentId] = installment.installmentName;
                  });
                }
              }
            }
          } catch (error) {
            console.error('Error fetching entity name:', error);
            entityNamesMap[membership.entity_Id] = `${isPackageType(membership.entity_Type) ? 'Package' : 'Series'} ${membership.entity_Id}`;
          }

          // Calculate minimum installmentId for sequential numbering
          const allInstallments = membership.installments || [];
          const minInstallmentId = allInstallments.length > 0
            ? Math.min(...allInstallments.map(inst => inst.installmentId))
            : null;

          processedMemberships.push({
            ...membership,
            unpaidInstallments,
            minInstallmentId
          });
        }
      }

      setUnbilledMemberships(processedMemberships);
      setEntityNames(entityNamesMap);
      setInstallmentNames(installmentNamesMap);
      setUnbilledServices(unbilledServicesData || []);
    } catch (err) {
      console.error('Error loading unbilled data:', err);
      setUnbilledMemberships([]);
      setUnbilledServices([]);
    } finally {
      setUnbilledLoading(false);
    }
  };

  // Handler for toggling installment selection
  const handleInstallmentToggle = (installmentId, membership, installment) => {
    const billedAmount = installment.billedAmount || installment.paidAmount || 0;
    const remainingAmount = Math.round((installment.amount - billedAmount) * 100) / 100;

    setSelectedInstallments(prev => {
      const isChecked = prev[installmentId]?.checked || false;
      return {
        ...prev,
        [installmentId]: {
          checked: !isChecked,
          amount: !isChecked ? remainingAmount : 0,
          membershipId: membership.membershipId || membership.MembershipId,
          entityId: membership.entity_Id,
          entityType: membership.entity_Type
        }
      };
    });
  };

  // Handler for toggling service selection
  const handleServiceToggle = (service) => {
    const bookingResourceDetailId = service.bookingResourceDetail_Id || service.BookingResourceDetail_Id;
    if (!bookingResourceDetailId) {
      console.error('No bookingResourceDetailId found in service:', service);
      return;
    }

    const calculatedAmount = service.calculatedAmount || service.CalculatedAmount || 0;
    const serviceNetAmount = service.serviceNetAmount || service.ServiceNetAmount || 0;
    const packageNetAmount = service.packageNetAmount || service.PackageNetAmount || 0;
    const amount = calculatedAmount || packageNetAmount || serviceNetAmount || 0;

    setSelectedUnbilledServices(prev => {
      const isChecked = prev[bookingResourceDetailId]?.checked || false;
      const newChecked = !isChecked;

      if (newChecked) {
        // When checking, store the service object and amount
        // If amount is 0, still allow selection (amount might be set later or could be a free service)
        return {
          ...prev,
          [bookingResourceDetailId]: {
            checked: true,
            amount: amount,
            service: service // Store full service object for navigation
          }
        };
      } else {
        // When unchecking, remove the entry
        const newState = { ...prev };
        delete newState[bookingResourceDetailId];
        return newState;
      }
    });
  };

  // Handler for select all installments
  const handleSelectAllInstallments = (checked) => {
    setSelectAllInstallments(checked);
    if (checked) {
      const newSelections = {};
      unbilledMemberships.forEach(membership => {
        membership.unpaidInstallments.forEach(installment => {
          const billedAmount = installment.billedAmount || installment.paidAmount || 0;
          const remainingAmount = Math.round((installment.amount - billedAmount) * 100) / 100;
          newSelections[installment.installmentId] = {
            checked: true,
            amount: remainingAmount,
            membershipId: membership.membershipId || membership.MembershipId,
            entityId: membership.entity_Id,
            entityType: membership.entity_Type
          };
        });
      });
      setSelectedInstallments(newSelections);
    } else {
      setSelectedInstallments({});
    }
  };

  // Handler for select all services
  const handleSelectAllServices = (checked) => {
    setSelectAllServices(checked);
    if (checked) {
      const newSelections = {};
      unbilledServices.forEach(service => {
        const bookingResourceDetailId = service.bookingResourceDetail_Id || service.BookingResourceDetail_Id;
        if (!bookingResourceDetailId) {
          console.error('No bookingResourceDetailId found in service:', service);
          return;
        }
        const calculatedAmount = service.calculatedAmount || service.CalculatedAmount || 0;
        const serviceNetAmount = service.serviceNetAmount || service.ServiceNetAmount || 0;
        const packageNetAmount = service.packageNetAmount || service.PackageNetAmount || 0;
        const amount = calculatedAmount || packageNetAmount || serviceNetAmount || 0;
        newSelections[bookingResourceDetailId] = {
          checked: true,
          amount: amount,
          service: service // Store full service object
        };
      });
      console.log('Select All Services - New selections:', newSelections);
      setSelectedUnbilledServices(newSelections);
    } else {
      setSelectedUnbilledServices({});
    }
  };

  // Handler for bill selected items
  const handleBillSelected = () => {
    const currentCustomerId = customer?.customer_Id || customerId;
    if (!currentCustomerId) {
      console.error('No customer ID available');
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Customer ID is missing. Please try again.',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Collect selected installments
    const selectedInstallmentsList = Object.entries(selectedInstallments)
      .filter(([_, data]) => {
        if (!data) return false;
        return data.checked === true && data.amount && data.amount > 0;
      })
      .map(([installmentId, data]) => ({
        installmentId: parseInt(installmentId),
        amount: data.amount,
        membershipId: data.membershipId,
        entityId: data.entityId,
        entityType: data.entityType
      }));

    // Collect selected services - check for checked status
    const selectedServicesList = Object.entries(selectedUnbilledServices)
      .filter(([_, data]) => {
        if (!data) return false;
        if (data.checked !== true) return false;
        if (!data.service) return false;
        return true;
      })
      .map(([_, data]) => ({
        ...data.service, // Spread the original service object
        selectedAmount: data.amount // Explicitly add the selected amount
      }));

    // Check if there are any selected items
    if (selectedInstallmentsList.length === 0 && selectedServicesList.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Items Selected',
        text: 'Please select at least one item to bill.',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Navigate to billing page with selected items
    try {
      navigate('/GmsBilling', {
        state: {
          fromCustomerProfile: true,
          customerId: currentCustomerId,
          selectedInstallments: selectedInstallmentsList,
          selectedUnbilledServices: selectedServicesList
        }
      });

      // Close the modal
      toggle();
    } catch (error) {
      console.error('Navigation error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Navigation Error',
        text: 'Failed to navigate to billing page. Please try again.',
        confirmButtonText: 'OK'
      });
    }
  };

  const loadPaymentHistory = async () => {
    try {
      setPaymentHistoryLoading(true);
      const currentCustomerId = customerId || customer?.customer_Id;
      if (!currentCustomerId) return;


      // Fetch receipts and credit invoices in parallel
      // BusinessId is now optional - if not provided, returns invoices across all businesses
      const [receiptsResponse, creditInvoicesData] = await Promise.all([
        // Fetch receipts for the customer
        getReceipts({
          Type: "cu", // Required: "cu" for customer receipts
          CustomerId: currentCustomerId,
          Page: 1,
          PageSize: 10000 // Get all receipts
        }).catch((err) => {
          console.error('❌ Error getting receipts:', err);
          return { receipts: [], totalCount: 0 };
        }),
        // Fetch credit invoices with correct outstanding amounts
        // BusinessId is optional - pass null to get invoices across all businesses
        // IncludePaidInvoices = true to show all invoices (paid and unpaid) for Payment History
        getCustomerCreditInvoices(currentCustomerId, null, null, true).catch((err) => {
          console.error('❌ Error getting credit invoices:', err);
          return [];
        })
      ]);

      const receiptsData = receiptsResponse?.receipts || receiptsResponse || [];
      setReceipts(Array.isArray(receiptsData) ? receiptsData : []);

      setCreditInvoices(Array.isArray(creditInvoicesData) ? creditInvoicesData : []);
    } catch (err) {
      console.error('❌ Error loading payment history:', err);
      setReceipts([]);
      setCreditInvoices([]);
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  const fetchWhatsAppCount = async () => {
    const passedCustomerId = customer?.customer_Id || customer?.value || customerId;
    const phoneNumber = customerData?.customer_ContactNo || customer?.customer_ContactNo || customerData?.customer_Mobile || customer?.customer_Mobile;
    
    if (!phoneNumber) {
      setWhatsappMessageCount(0);
      return;
    }
    
    try {
      setWhatsappCountLoading(true);
      // Remove any non-numeric characters for the endpoint
      const cleanPhone = phoneNumber.trim().replace(/^0+/, '').replace(/\D/g, '');
      const response = await axios.get(`${BASE_URL}/api/WhatsAppWebhook/count/${cleanPhone}`);
      setWhatsappMessageCount(response.data);
    } catch (err) {
      console.error('Error fetching WhatsApp count:', err);
      setWhatsappMessageCount(0);
    } finally {
      setWhatsappCountLoading(false);
    }
  };

  const loadSoapNotes = async (customerId) => {
    try {
      setSoapNotesLoading(true);
      const payload = {
        Customer_Id: customerId
      };
      const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      const response = await axios.post(`${BASE_URL}/api/Soap/list`, payload, authHeader);
      setSoapNotes(response.data || []);
    } catch (err) {
      console.error('Error loading SOAP notes:', err);
      setSoapNotes([]);
    } finally {
      setSoapNotesLoading(false);
    }
  };

  const loadWhatsAppMessages = async () => {
    const phoneNumber = customerData?.customer_ContactNo || customer?.customer_ContactNo || customerData?.customer_Mobile || customer?.customer_Mobile;
    
    if (!phoneNumber) return;
    
    try {
      setWhatsappMessagesLoading(true);
      const cleanPhone = phoneNumber.trim().replace(/^0+/, '').replace(/\D/g, '');
      const response = await axios.get(`${BASE_URL}/api/WhatsAppWebhook/messages-by-number/${cleanPhone}`);
      setWhatsappMessages(response.data);
      setShowMessages(true);
    } catch (err) {
      console.error('Error fetching WhatsApp messages:', err);
      if (typeof Swal !== 'undefined') {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load WhatsApp messages.'
        });
      }
    } finally {
      setWhatsappMessagesLoading(false);
    }
  };

  const loadFinancialData = async (customerId) => {
    try {

      // Only send customerId - no businessId or branchId
      const [advance, credit] = await Promise.all([
        getCustomerAdvanceBalance(customerId).catch((err) => {
          console.error('❌ Error getting advance balance:', err);
          return null;
        }),
        getCustomerOutstandingCredit(customerId).catch((err) => {
          console.error('❌ Error getting outstanding credit:', err);
          return null;
        })
      ]);


      setAdvanceBalance(advance);
      setOutstandingCredit(credit);

      // Fetch loyalty balance - BranchId and BusinessId can be null as the proc returns global balance
      try {
        const loyaltyData = await getCustomerBranchLoyaltyBalance(customerId, null, null);
        // Handle both object and single number responses
        const balanceValue = (typeof loyaltyData === 'object' && loyaltyData !== null)
          ? (loyaltyData.pointsBalance || loyaltyData.PointsBalance || 0)
          : (typeof loyaltyData === 'number' ? loyaltyData : 0);
        setLoyaltyBalance(balanceValue);
      } catch (err) {
        console.error('❌ Error getting loyalty balance:', err);
      }
    } catch (err) {
      console.error('❌ Error loading financial data:', err);
    }
  };

  // Calculate metrics
  const calculateMetrics = () => {
    const now = new Date();
    const periodStart = new Date();

    // Calculate period start based on timePeriod
    if (timePeriod === '1') {
      periodStart.setMonth(periodStart.getMonth() - 1);
    } else if (timePeriod === '3') {
      periodStart.setMonth(periodStart.getMonth() - 3);
    } else if (timePeriod === '6') {
      periodStart.setMonth(periodStart.getMonth() - 6);
    } else if (timePeriod === '12') {
      periodStart.setMonth(periodStart.getMonth() - 12);
    }

    // Filter bookings within period
    const periodBookings = allBookings.filter(booking => {
      const bookingDate = new Date(booking.booking_Date || booking.entry_Date);
      return bookingDate >= periodStart;
    });

    // Total visits (completed bookings)
    const totalVisits = periodBookings.filter(b => !b.is_Cancelled).length;

    // Open appointments (future bookings not cancelled)
    const openAppointments = allBookings.filter(booking => {
      // Use booking_Date with from_Time, or from_Time, or entry_Date as fallback
      let appointmentDateTime = null;
      if (booking.from_Time) {
        appointmentDateTime = new Date(booking.from_Time);
      } else if (booking.booking_Date) {
        appointmentDateTime = new Date(booking.booking_Date);
      } else {
        appointmentDateTime = new Date(booking.entry_Date);
      }
      return appointmentDateTime >= now && !booking.is_Cancelled;
    }).length;

    // Amount due (outstanding credit)
    const amountDue = outstandingCredit?.totalOutstanding || outstandingCredit?.TotalOutstanding || 0;

    // Membership balance (calculate from memberships)
    let membershipBalance = 0;
    memberships.forEach(membership => {
      if (membership.installments && Array.isArray(membership.installments)) {
        membership.installments.forEach(installment => {
          const available = (installment.amount || 0) - (installment.paidAmount || installment.billedAmount || 0);
          if (available > 0) {
            membershipBalance += available;
          }
        });
      }
    });

    return {
      totalVisits,
      openAppointments,
      amountDue,
      membershipBalance
    };
  };

  const metrics = calculateMetrics();

  // Get upcoming appointments
  const getUpcomingAppointments = () => {
    const now = new Date();
    return allBookings
      .filter(booking => {
        // Use booking_Date with from_Time, or from_Time, or entry_Date as fallback
        let appointmentDateTime = null;
        if (booking.from_Time) {
          appointmentDateTime = new Date(booking.from_Time);
        } else if (booking.booking_Date && booking.from_Time) {
          // Combine booking_Date with from_Time if from_Time is just time
          const bookingDate = new Date(booking.booking_Date);
          const fromTime = new Date(booking.from_Time);
          appointmentDateTime = new Date(bookingDate);
          appointmentDateTime.setHours(fromTime.getHours(), fromTime.getMinutes(), fromTime.getSeconds());
        } else if (booking.booking_Date) {
          appointmentDateTime = new Date(booking.booking_Date);
        } else {
          appointmentDateTime = new Date(booking.entry_Date);
        }

        return appointmentDateTime >= now && !booking.is_Cancelled;
      })
      .sort((a, b) => {
        const aTime = a.from_Time ? new Date(a.from_Time) : (a.booking_Date ? new Date(a.booking_Date) : new Date(a.entry_Date));
        const bTime = b.from_Time ? new Date(b.from_Time) : (b.booking_Date ? new Date(b.booking_Date) : new Date(b.entry_Date));
        return aTime - bTime;
      })
      .slice(0, 5);
  };

  // Get past appointments
  const getPastAppointments = () => {
    const now = new Date();
    return allBookings
      .filter(booking => {
        // Use booking_Date with from_Time, or from_Time, or entry_Date as fallback
        let appointmentDateTime = null;
        if (booking.from_Time) {
          appointmentDateTime = new Date(booking.from_Time);
        } else if (booking.booking_Date && booking.from_Time) {
          const bookingDate = new Date(booking.booking_Date);
          const fromTime = new Date(booking.from_Time);
          appointmentDateTime = new Date(bookingDate);
          appointmentDateTime.setHours(fromTime.getHours(), fromTime.getMinutes(), fromTime.getSeconds());
        } else if (booking.booking_Date) {
          appointmentDateTime = new Date(booking.booking_Date);
        } else {
          appointmentDateTime = new Date(booking.entry_Date);
        }

        return appointmentDateTime < now || booking.is_Cancelled;
      })
      .sort((a, b) => {
        const aTime = a.from_Time ? new Date(a.from_Time) : (a.booking_Date ? new Date(a.booking_Date) : new Date(a.entry_Date));
        const bTime = b.from_Time ? new Date(b.from_Time) : (b.booking_Date ? new Date(b.booking_Date) : new Date(b.entry_Date));
        return bTime - aTime;
      })
      .slice(0, 10);
  };

  // Get employee name
  const getEmployeeName = (employeeId) => {
    if (!employeeId) return '-';

    const employee = employees.find(e =>
      e.employee_Id === employeeId ||
      e.employeeId === employeeId ||
      e.value === employeeId ||
      e.id === employeeId
    );

    if (employee) {
      return employee.employee_NameEn || employee.employeeName_En || employee.label || employee.name || '-';
    }

    return '-';
  };

  // Get service name from service ID
  const getProductNameById = (productId) => {
    if (!productId) return '-';
    const product = products.find(s => s.product_Id === productId);
    return product ? product.product_NameEn : '-';
  };

  const getServiceNameById = (serviceId) => {
    if (!serviceId) return '-';
    const service = services.find(s =>
      s.service_Id === serviceId ||
      s.spaService_Id === serviceId ||
      s.value === serviceId ||
      s.id === serviceId
    );
    if (service) {
      return service.spaServiceName_En || service.spaService_NameEn || service.service_NameEn || service.serviceName_En || service.label || service.name || '-';
    }
    return '-';
  };

  // Get service cost from service ID
  const getServiceCostById = (serviceId) => {
    if (!serviceId) return 0;
    const service = services.find(s =>
      s.service_Id === serviceId ||
      s.spaService_Id === serviceId ||
      s.value === serviceId ||
      s.id === serviceId
    );
    if (service) {
      return parseFloat(service.cost || service.Cost || 0);
    }
    return 0;
  };

  // Expand bookings into individual service rows
  const expandBookingsToServices = (bookings) => {
    const serviceRows = [];
    bookings.forEach((booking, bookingIndex) => {
      // Get appointment date from booking
      const appointmentDate = booking.from_Time
        ? new Date(booking.from_Time).toISOString().split('T')[0]
        : (booking.booking_Date
          ? new Date(booking.booking_Date).toISOString().split('T')[0]
          : booking.entry_Date);

      // Check if booking has details with resourceDetails
      if (booking.details && Array.isArray(booking.details) && booking.details.length > 0) {
        booking.details.forEach((detail, detailIndex) => {
          if (detail.resourceDetails && Array.isArray(detail.resourceDetails) && detail.resourceDetails.length > 0) {
            // Create a row for each service in resourceDetails
            detail.resourceDetails.forEach((resource, resourceIndex) => {
              const serviceName = resource.service_Id ? getServiceNameById(resource.service_Id) : '-';
              const serviceCost = resource.service_Id ? getServiceCostById(resource.service_Id) : 0;
              const serviceTime = resource.from_Time && resource.to_Time
                ? `${new Date(resource.from_Time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - ${new Date(resource.to_Time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                : '-';
              const employeeName = resource.employee_Id ? getEmployeeName(resource.employee_Id) : '-';

              // Calculate duration
              let duration = null;
              if (resource.from_Time && resource.to_Time) {
                duration = Math.round((new Date(resource.to_Time) - new Date(resource.from_Time)) / (1000 * 60));
              }

              serviceRows.push({
                booking_Id: booking.booking_Id,
                bookingIndex: bookingIndex,
                detailIndex: detailIndex,
                resourceIndex: resourceIndex,
                date: appointmentDate,
                serviceName: serviceName + (duration ? ` (${duration}mins)` : ''),
                serviceCost: serviceCost,
                time: serviceTime,
                staff: employeeName,
                booking: booking,
                resource: resource
              });
            });
          } else {
            // If no resourceDetails, create a single row for the booking
            const serviceName = getServiceName(booking);
            // Try to get service cost from first resource detail if available
            let serviceCost = 0;
            if (detail.resourceDetails && detail.resourceDetails.length > 0 && detail.resourceDetails[0].service_Id) {
              serviceCost = getServiceCostById(detail.resourceDetails[0].service_Id);
            } else if (detail.entity_Type === 'ser' || detail.entity_Type === 'Service') {
              serviceCost = getServiceCostById(detail.entity_Id);
            }
            const serviceTime = booking.from_Time && booking.to_Time
              ? `${new Date(booking.from_Time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - ${new Date(booking.to_Time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
              : '-';
            const employeeName = booking.details && booking.details[0]?.resourceDetails?.[0]?.employee_Id
              ? getEmployeeName(booking.details[0].resourceDetails[0].employee_Id)
              : '-';

            serviceRows.push({
              booking_Id: booking.booking_Id,
              bookingIndex: bookingIndex,
              detailIndex: detailIndex,
              resourceIndex: 0,
              date: appointmentDate,
              serviceName: serviceName,
              serviceCost: serviceCost,
              time: serviceTime,
              staff: employeeName,
              booking: booking,
              resource: null
            });
          }
        });
      } else {
        // If no details, create a single row for the booking
        const serviceName = getServiceName(booking);
        const serviceCost = 0; // No service ID available
        const serviceTime = booking.from_Time && booking.to_Time
          ? `${new Date(booking.from_Time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - ${new Date(booking.to_Time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
          : '-';
        const employeeName = '-';

        serviceRows.push({
          booking_Id: booking.booking_Id,
          bookingIndex: bookingIndex,
          detailIndex: 0,
          resourceIndex: 0,
          date: appointmentDate,
          serviceName: serviceName,
          serviceCost: serviceCost,
          time: serviceTime,
          staff: employeeName,
          booking: booking,
          resource: null
        });
      }
    });
    return serviceRows;
  };

  // Get entity name from membership
  const getEntityName = (membership) => {
    const entityType = membership.entity_Type || membership.entityType;
    const entityId = membership.entity_Id || membership.entityId;

    if (!entityType || !entityId) {
      console.log('⚠️ Missing entity type or ID');
      return '-';
    }

    // Check if entity_Type is numeric (1=Service, 2=Package, 3=PackageSeries) or string (SER, PAC, etc.)
    if (entityType === 1 || entityType === 'SER' || entityType === 'ser' || entityType === 'Service') {
      // Service - but memberships are usually packages or series, not services directly
      console.log('⚠️ Service type in membership - unusual');
      return 'Service';
    } else if (entityType === 2 || entityType === 'PAC' || entityType === 'pac' || entityType === 'Package') {
      const pkg = packages.find(p =>
        p.package_Id === entityId ||
        p.packageId === entityId ||
        p.value === entityId ||
        p.id === entityId
      );
      console.log('✅ Found package:', pkg);
      if (pkg) {
        return pkg.packageName_En || pkg.package_NameEn || pkg.label || pkg.name || 'Package';
      }
    } else if (entityType === 3 || entityType === 'SER' || entityType === 'sep' || entityType === 'PackageSeries') {
      const series = packageSeries.find(s =>
        s.series_Id === entityId ||
        s.seriesId === entityId ||
        s.value === entityId ||
        s.id === entityId
      );
      if (series) {
        return series.seriesName_En || series.series_NameEn || series.label || series.name || 'Package Series';
      }
    }

    console.log('⚠️ Could not find entity name');
    return '-';
  };

  // Get products from invoices
  const getProductsFromInvoices = () => {

    const productMap = new Map();

    invoices.forEach(invoice => {
      // Check if invoice has selectedProducts array
      if (invoice.selectedProducts && Array.isArray(invoice.selectedProducts)) {
        invoice.selectedProducts.forEach(product => {
          const productId = product.product_Id || product.productId || product.id;
          const quantity = product.quantity || product.qty || 0;

          if (productId) {
            const existing = productMap.get(productId) || { product, totalQuantity: 0, totalAmount: 0 };
            existing.totalQuantity += quantity;
            existing.totalAmount += (product.price || product.amount || 0) * quantity;
            productMap.set(productId, existing);
          }
        });
      }

      // Check invoice details (if structured differently)
      if (invoice.details && Array.isArray(invoice.details)) {
        invoice.details.forEach((detail, index) => {
          // Check for product in detail
          if (detail.product_Id || detail.productId) {
            const productId = detail.product_Id || detail.productId;
            const quantity = detail.quantity || detail.qty || 1;
            const price = detail.price || detail.amount || detail.netAmount || 0;

            const existing = productMap.get(productId) || {
              product: detail,
              totalQuantity: 0,
              totalAmount: 0
            };
            existing.totalQuantity += quantity;
            existing.totalAmount += price * quantity;
            productMap.set(productId, existing);
          }

          // Also check if detail has entityType or entity_Type === 'pro' or 'product'
          const entityType = detail.entityType || detail.entity_Type;
          if (entityType === 'pro' || entityType === 'product' || entityType === 'Product') {
            const productId = detail.entityId || detail.entity_Id;
            if (productId) {
              const quantity = detail.quantity || detail.qty || 1;
              const price = detail.price || detail.amount || detail.netAmount || 0;

              // Convert productId to number for consistent comparison
              const productIdNum = parseInt(productId, 10);
              const existing = productMap.get(productIdNum) || {
                product: detail,
                productId: productIdNum,
                totalQuantity: 0,
                totalAmount: 0
              };
              existing.totalQuantity += quantity;
              existing.totalAmount += price * quantity;
              productMap.set(productIdNum, existing);
            }
          }
        });
      }
    });

    return Array.from(productMap.values());
  };

  // Get service name from booking
  const getServiceName = (booking) => {

    if (!booking.details || !Array.isArray(booking.details) || booking.details.length === 0) {
      console.log('⚠️ No details found in booking');
      return '-';
    }

    // Get first detail
    const firstDetail = booking.details[0];

    // Check entity_Type first - this tells us if it's a package, service, or package series
    if (firstDetail.entity_Type) {

      // If it's a package
      if (firstDetail.entity_Type === 'pac' || firstDetail.entity_Type === 'Package') {
        const packageId = firstDetail.entity_Id;
        const pkg = packages.find(p =>
          p.package_Id === packageId ||
          p.packageId === packageId ||
          p.value === packageId ||
          p.id === packageId
        );
        console.log('✅ Found package:', pkg);
        if (pkg) {
          const packageName = pkg.packageName_En || pkg.package_NameEn || pkg.label || pkg.name || 'Package';

          // Get all services within this package
          const serviceNames = [];
          let totalDuration = 0;

          if (firstDetail.resourceDetails && Array.isArray(firstDetail.resourceDetails)) {
            firstDetail.resourceDetails.forEach(resource => {
              if (resource.service_Id) {
                const service = services.find(s =>
                  s.service_Id === resource.service_Id ||
                  s.spaService_Id === resource.service_Id ||
                  s.value === resource.service_Id ||
                  s.id === resource.service_Id
                );

                if (service) {
                  const serviceName = service.spaServiceName_En || service.spaService_NameEn || service.service_NameEn || service.serviceName_En || service.label || service.name || 'Service';
                  let serviceDuration = 0;

                  if (resource.from_Time && resource.to_Time) {
                    serviceDuration = Math.round((new Date(resource.to_Time) - new Date(resource.from_Time)) / (1000 * 60));
                    totalDuration += serviceDuration;
                  }

                  serviceNames.push(serviceName + (serviceDuration > 0 ? ` (${serviceDuration}mins)` : ''));
                }
              }
            });
          }


          // Format: "Package Name - Service 1 (55mins), Service 2 (90mins) (145mins total)"
          if (serviceNames.length > 0) {
            return packageName + ' - ' + serviceNames.join(', ') + (totalDuration > 0 ? ` (${totalDuration}mins total)` : '');
          } else {
            return packageName + (totalDuration > 0 ? ` (${totalDuration}mins)` : '');
          }
        }
      }

      // If it's a package series (sep = series)
      if (firstDetail.entity_Type === 'sep' || firstDetail.entity_Type === 'PackageSeries') {
        const seriesId = firstDetail.entity_Id;
        const series = packageSeries.find(s =>
          s.series_Id === seriesId ||
          s.seriesId === seriesId ||
          s.value === seriesId ||
          s.id === seriesId
        );
        if (series) {
          const seriesName = series.seriesName_En || series.series_NameEn || series.label || series.name || 'Package Series';
          return seriesName;
        }
      }

      // If it's a service (ser = service)
      if (firstDetail.entity_Type === 'ser' || firstDetail.entity_Type === 'Service') {
        const serviceId = firstDetail.entity_Id;
        console.log('🔍 Looking for service ID from entity:', serviceId);
        const service = services.find(s =>
          s.service_Id === serviceId ||
          s.spaService_Id === serviceId ||
          s.value === serviceId ||
          s.id === serviceId
        );
        if (service) {
          const serviceName = service.spaServiceName_En || service.spaService_NameEn || service.service_NameEn || service.serviceName_En || service.label || 'Service';
          console.log('✅ Service name resolved:', serviceName);
          return serviceName;
        }
      }
    }

    // Fallback: Check resource details
    if (firstDetail.resourceDetails && Array.isArray(firstDetail.resourceDetails) && firstDetail.resourceDetails.length > 0) {
      const firstResource = firstDetail.resourceDetails[0];
      console.log('🔧 First resource:', firstResource);
      console.log('📦 Available services:', services.length);
      console.log('📦 Available packages:', packages.length);

      // Check for package in resource
      if (firstResource.package_Id) {
        console.log('🔍 Looking for package ID from resource:', firstResource.package_Id);
        const pkg = packages.find(p =>
          p.package_Id === firstResource.package_Id ||
          p.packageId === firstResource.package_Id ||
          p.value === firstResource.package_Id ||
          p.id === firstResource.package_Id
        );
        console.log('✅ Found package:', pkg);

        if (pkg) {
          const packageName = pkg.packageName_En || pkg.package_NameEn || pkg.label || pkg.name || 'Package';
          console.log('✅ Package name resolved:', packageName);
          return packageName;
        }
      }

      // Check for service in resource
      if (firstResource.service_Id) {
        console.log('🔍 Looking for service ID from resource:', firstResource.service_Id);
        const service = services.find(s =>
          s.service_Id === firstResource.service_Id ||
          s.spaService_Id === firstResource.service_Id ||
          s.value === firstResource.service_Id ||
          s.id === firstResource.service_Id
        );
        console.log('✅ Found service:', service);

        if (service) {
          // Calculate total duration from all resources in this detail
          let totalDuration = 0;
          if (firstDetail.resourceDetails.length > 1) {
            // Multiple services - calculate total duration
            firstDetail.resourceDetails.forEach(resource => {
              if (resource.from_Time && resource.to_Time) {
                const duration = Math.round((new Date(resource.to_Time) - new Date(resource.from_Time)) / (1000 * 60));
                totalDuration += duration;
              }
            });
          } else {
            // Single service - use its duration
            if (firstResource.from_Time && firstResource.to_Time) {
              totalDuration = Math.round((new Date(firstResource.to_Time) - new Date(firstResource.from_Time)) / (1000 * 60));
            }
          }

          const serviceName = service.spaServiceName_En || service.spaService_NameEn || service.service_NameEn || service.serviceName_En || service.label || service.name || 'Service';
          console.log('✅ Service name resolved:', serviceName, 'Duration:', totalDuration);
          return serviceName + (totalDuration > 0 ? ` (${totalDuration}mins)` : '');
        }
      }

    }

    console.log('⚠️ Could not find service name');
    return '-';
  };

  // Get booking amount (from invoice if linked, otherwise from service amounts)
  const getBookingAmount = (booking) => {
    console.log('🔍 Getting booking amount for booking:', booking.booking_Id);

    // First, try to get from invoice if linked
    if (booking.invoice_Id) {
      const invoice = invoices.find(inv => inv.Invoice_Id === booking.invoice_Id);
      if (invoice) {
        console.log('✅ Found invoice amount:', invoice.Invoice_NetAmount);
        return parseFloat(invoice.Invoice_NetAmount || 0);
      }
    }

    // If no invoice, calculate from service amounts
    let total = 0;
    if (booking.details && Array.isArray(booking.details)) {
      booking.details.forEach(detail => {
        // Check if it's a package - use entity_Id to find package
        if (detail.entity_Type === 'pac' || detail.entity_Type === 'Package') {
          const packageId = detail.entity_Id;
          const pkg = packages.find(p =>
            p.package_Id === packageId ||
            p.packageId === packageId ||
            p.value === packageId ||
            p.id === packageId
          );
          if (pkg) {
            const packageAmount = parseFloat(pkg.packageAmount || pkg.amount || pkg.netAmount || 0);
            console.log('📦 Found package amount:', packageAmount);
            total += packageAmount;
          }
        }
        // Check if it's a service - use entity_Id to find service
        else if (detail.entity_Type === 'ser' || detail.entity_Type === 'Service') {
          const serviceId = detail.entity_Id;
          const service = services.find(s =>
            s.service_Id === serviceId ||
            s.spaService_Id === serviceId ||
            s.value === serviceId ||
            s.id === serviceId
          );
          if (service) {
            const serviceAmount = parseFloat(service.amount || service.netAmount || service.spaServiceAmount || 0);
            console.log('🔧 Found service amount:', serviceAmount);
            total += serviceAmount;
          }
        }
        // If no entity type, try to get from resourceDetails
        else if (detail.resourceDetails && Array.isArray(detail.resourceDetails)) {
          detail.resourceDetails.forEach(resource => {
            // First try resource.cost
            if (resource.cost && parseFloat(resource.cost) > 0) {
              total += parseFloat(resource.cost);
            }
            // Otherwise, look up service by service_Id
            else if (resource.service_Id) {
              const service = services.find(s =>
                s.service_Id === resource.service_Id ||
                s.spaService_Id === resource.service_Id ||
                s.value === resource.service_Id ||
                s.id === resource.service_Id
              );
              if (service) {
                const serviceAmount = parseFloat(service.amount || service.netAmount || service.spaServiceAmount || 0);
                console.log('🔧 Found service amount from resource:', serviceAmount);
                total += serviceAmount;
              }
            }
            // Check for package
            else if (resource.package_Id) {
              const pkg = packages.find(p =>
                p.package_Id === resource.package_Id ||
                p.packageId === resource.package_Id ||
                p.value === resource.package_Id ||
                p.id === resource.package_Id
              );
              if (pkg) {
                const packageAmount = parseFloat(pkg.packageAmount || pkg.amount || pkg.netAmount || 0);
                console.log('📦 Found package amount from resource:', packageAmount);
                total += packageAmount;
              }
            }
          });
        }
      });
    }

    console.log('💰 Total booking amount:', total);
    return total;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '/');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  const getBusinessName = () => {
    const businessId = localStorage.getItem('businessId');
    if (businessId && businesses.length > 0) {
      const business = businesses.find(b => b.business_Id === parseInt(businessId));
      return business ? business.business_NameEn : '-';
    }
    return '-';
  };

  const getInitials = () => {
    if (!customerData?.customer_NameEn) return 'C';
    const names = customerData.customer_NameEn.split(' ');
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }
    return customerData.customer_NameEn.charAt(0).toUpperCase();
  };

  // Toggle membership row expansion
  const toggleMembershipExpansion = (membershipId) => {
    const newExpanded = new Set(expandedMemberships);
    if (newExpanded.has(membershipId)) {
      newExpanded.delete(membershipId);
    } else {
      newExpanded.add(membershipId);
    }
    setExpandedMemberships(newExpanded);
  };

  // Process membership details to get services and products with quantities
  const getMembershipDetails = (membership) => {
    if (!membership.details || !Array.isArray(membership.details) || membership.details.length === 0) {
      return { services: [], products: [] };
    }

    // Group by package, then by service/product
    const groupedByPackage = membership.details.reduce((acc, detail) => {
      const packageKey = detail.packageId ? `pkg_${detail.packageId}` : 'direct';
      const packageId = detail.packageId || null;
      let packageName = 'Direct Items';
      if (packageId) {
        const pkg = packages.find(p =>
          p.package_Id === packageId ||
          p.packageId === packageId ||
          p.value === packageId ||
          p.id === packageId
        );
        packageName = pkg ? (pkg.packageName_En || pkg.package_NameEn || `Package ${packageId}`) : `Package ${packageId}`;
      }

      if (!acc[packageKey]) {
        acc[packageKey] = {
          packageId: packageId,
          packageName: packageName,
          services: {},
          products: {}
        };
      }

      // Process services
      if (detail.serviceId) {
        const serviceKey = `svc_${detail.serviceId}`;
        if (!acc[packageKey].services[serviceKey]) {
          // Initialize remainingQty from the first detail's RemainingQty value
          // If not provided, we'll use pendingQty later (all pending are available)
          const initialRemainingQty = detail.remainingQty !== undefined && detail.remainingQty !== null
            ? detail.remainingQty
            : null; // Use null instead of 0 to distinguish "not set" from "actually 0"

          acc[packageKey].services[serviceKey] = {
            serviceId: detail.serviceId,
            serviceName: getServiceNameById(detail.serviceId),
            membershipId: detail.membershipId || membership.membershipId,
            totalQty: 0,
            availableQty: 0,
            bookedQty: 0,  // NEW: Booked quantity (non-cancelled, non-completed bookings)
            completedQty: 0,
            inProgressQty: 0,
            pendingQty: 0,
            remainingQty: initialRemainingQty  // Store RemainingQty from API (null if not provided)
          };
        }
        acc[packageKey].services[serviceKey].totalQty++;
        const status = (detail.completionStatus || 'Pending').toString().trim().toLowerCase();
        if (status === 'completed' || status === 'complete') {
          acc[packageKey].services[serviceKey].completedQty++;
        } else if (status === 'in progress' || status === 'inprogress' || status === 'check in' || status === 'checkin') {
          acc[packageKey].services[serviceKey].inProgressQty++;
        } else {
          acc[packageKey].services[serviceKey].pendingQty++;
          // Update remainingQty: For pending services, RemainingQty should be the same for all details in the same group
          // Use the first non-null value we encounter (all details in same group should have same RemainingQty)
          const detailRemainingQty = detail.remainingQty !== undefined && detail.remainingQty !== null
            ? detail.remainingQty
            : detail.RemainingQty !== undefined && detail.RemainingQty !== null
              ? detail.RemainingQty
              : null;

          if (detailRemainingQty !== null && detailRemainingQty !== undefined) {
            // If we haven't set remainingQty yet (null), or if this detail has a valid value, use it
            // For the same membership+service+package, all details should have the same RemainingQty
            if (acc[packageKey].services[serviceKey].remainingQty === null ||
              (detailRemainingQty > 0 && acc[packageKey].services[serviceKey].remainingQty === 0)) {
              acc[packageKey].services[serviceKey].remainingQty = detailRemainingQty;
            }
          }
        }
      }

      // Process products
      if (detail.productId) {
        const productKey = `prod_${detail.productId}`;
        if (!acc[packageKey].products[productKey]) {
          acc[packageKey].products[productKey] = {
            productId: detail.productId,
            productName: getProductNameById(detail.productId),
            totalQty: 0,
            availableQty: 0,
            completedQty: 0,
            inProgressQty: 0,
            pendingQty: 0
          };
        }
        acc[packageKey].products[productKey].totalQty++;
        const status = (detail.completionStatus || 'Pending').toString().trim().toLowerCase();
        if (status === 'completed' || status === 'complete') {
          acc[packageKey].products[productKey].completedQty++;
        } else if (status === 'in progress' || status === 'inprogress' || status === 'check in' || status === 'checkin') {
          acc[packageKey].products[productKey].inProgressQty++;
        } else {
          acc[packageKey].products[productKey].pendingQty++;
          acc[packageKey].products[productKey].availableQty++;
        }
      }

      return acc;
    }, {});

    // Convert to arrays and calculate booked quantity
    const result = { services: [], products: [] };

    Object.values(groupedByPackage).forEach(packageData => {
      // Add services
      Object.values(packageData.services).forEach(service => {
        // Calculate booked quantity: Total Pending - RemainingQty
        // RemainingQty from API already accounts for booked services
        // So: Booked = Pending - RemainingQty
        // IMPORTANT: 
        // - If RemainingQty is not provided (undefined/null), assume all pending are available (bookedQty = 0)
        // - If RemainingQty = 0 and pendingQty > 0, it could mean:
        //   a) All are booked (bookedQty = pendingQty), OR
        //   b) RemainingQty calculation is wrong (should be pendingQty if nothing booked)
        //   We need to check if there are actual bookings to distinguish
        // For now, if RemainingQty = 0 and pendingQty > 0, we'll assume it's a calculation issue
        // and set bookedQty = 0 (safer assumption - if nothing is actually booked, show 0)
        let remainingQty = service.remainingQty;
        if (remainingQty === undefined || remainingQty === null) {
          // RemainingQty not provided - assume all pending are available
          remainingQty = service.pendingQty;
        } else if (remainingQty === 0 && service.pendingQty > 0) {
          // RemainingQty = 0 but there are pending services
          // This could mean all are booked OR RemainingQty calculation is wrong
          // Since we can't query bookings from frontend, we'll be conservative:
          // If RemainingQty = 0, assume it's a calculation issue and all are available
          // (This handles the case where bookings exist but Membership_Id is NULL)
          remainingQty = service.pendingQty;
        }
        const bookedQty = Math.max(0, service.pendingQty - remainingQty);

        // Use RemainingQty from API for availableQty (more accurate than counting)
        // If RemainingQty is 0 or not provided, use pendingQty (all pending are available)
        const availableQty = (service.remainingQty !== undefined && service.remainingQty !== null && service.remainingQty > 0)
          ? service.remainingQty
          : service.pendingQty;

        result.services.push({
          ...service,
          packageId: packageData.packageId,
          packageName: packageData.packageName,
          bookedQty: bookedQty,  // Calculated booked quantity
          availableQty: availableQty  // Use RemainingQty from API
        });
      });

      // Add products
      Object.values(packageData.products).forEach(product => {
        result.products.push({
          ...product,
          packageId: packageData.packageId,
          packageName: packageData.packageName
        });
      });
    });

    return result;
  };

  if (!isOpen) return null;

  const upcomingAppointments = getUpcomingAppointments();
  const pastAppointments = getPastAppointments();

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="xl"
        className="customer-profile-modal"
        backdrop="static"
        style={{ maxWidth: '95%' }}
      >
      <ModalHeader toggle={toggle} className="bg-light border-bottom">
        <div className="d-flex align-items-center justify-content-between w-100">
          <h5 className="mb-0">
            {customerData?.customer_NameEn || 'Customer Profile'}
          </h5>
        </div>
      </ModalHeader>
      <ModalBody style={{ maxHeight: '85vh', overflowY: 'auto', padding: 0 }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading customer data...</p>
          </div>
        ) : error ? (
          <Alert color="danger" className="m-3">{error}</Alert>
        ) : !customerData ? (
          <Alert color="warning" className="m-3">No customer data available</Alert>
        ) : (
          <div>
            {/* Top Navigation Tabs */}
            <Nav tabs className="border-bottom px-3" style={{ backgroundColor: '#f8f9fa' }}>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'overview' })}
                  onClick={() => setActiveTab('overview')}
                  style={{ cursor: 'pointer' }}
                >
                  Overview
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'profile' })}
                  onClick={() => setActiveTab('profile')}
                  style={{ cursor: 'pointer' }}
                >
                  Profile
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'notes' })}
                  onClick={() => setActiveTab('notes')}
                  style={{ cursor: 'pointer' }}
                >
                  Notes
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'soapNotes' })}
                  onClick={() => setActiveTab('soapNotes')}
                  style={{ cursor: 'pointer' }}
                >
                  SOAP Notes
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'appointment' })}
                  onClick={() => setActiveTab('appointment')}
                  style={{ cursor: 'pointer' }}
                >
                  Appointment
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'products' })}
                  onClick={() => setActiveTab('products')}
                  style={{ cursor: 'pointer' }}
                >
                  Products
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'memberships' })}
                  onClick={() => setActiveTab('memberships')}
                  style={{ cursor: 'pointer' }}
                >
                  Memberships ({memberships.filter(m => {
                    const entityType = m.entity_Type || m.entityType;
                    return entityType === 3 ||
                      entityType === 'sep' || entityType === 'SER' || entityType === 'PackageSeries';
                  }).length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'packages' })}
                  onClick={() => setActiveTab('packages')}
                  style={{ cursor: 'pointer' }}
                >
                  Packages ({memberships.filter(m => {
                    const entityType = m.entity_Type || m.entityType;
                    return entityType === 2 ||
                      entityType === 'pac' || entityType === 'PAC' || entityType === 'Package';
                  }).length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'unbilled' })}
                  onClick={() => setActiveTab('unbilled')}
                  style={{ cursor: 'pointer' }}
                >
                  Unbilled Services
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'paymentHistory' })}
                  onClick={() => setActiveTab('paymentHistory')}
                  style={{ cursor: 'pointer' }}
                >
                  Payment History
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'whatsappMessages' })}
                  onClick={() => {
                    setActiveTab('whatsappMessages');
                    setShowMessages(false);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  WhatsApp Messages
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab} className="p-3">
              {/* Overview Tab */}
              <TabPane tabId="overview">
                <Row>
                  {/* Left Sidebar - Customer Summary */}
                  <Col md={4} className="border-end pe-3">
                    <Card className="border-0 shadow-sm mb-3">
                      <CardBody className="text-center">
                        <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                          style={{ width: '80px', height: '80px', fontSize: '32px', fontWeight: 'bold' }}>
                          {getInitials()}
                        </div>
                        <h5 className="mb-2">{customerData.customer_NameEn}</h5>
                        <p className="text-muted mb-3">
                          <Phone size={14} className="me-1" />
                          {customerData.customer_ContactNo || '-'}
                        </p>

                        <div className="mb-3">
                          <Select
                            value={{ value: timePeriod, label: timePeriod === '1' ? 'Last 1 Month' : timePeriod === '3' ? 'Last 3 Months' : timePeriod === '6' ? 'Last 6 Months' : 'Last 12 Months' }}
                            onChange={(option) => setTimePeriod(option.value)}
                            options={[
                              { value: '1', label: 'Last 1 Month' },
                              { value: '3', label: 'Last 3 Months' },
                              { value: '6', label: 'Last 6 Months' },
                              { value: '12', label: 'Last 12 Months' }
                            ]}
                            classNamePrefix="select2-selection"
                            isSearchable={false}
                          />
                        </div>

                        <div className="text-start">
                          <div className="mb-2">
                            <strong>Total visits:</strong> {metrics.totalVisits}
                          </div>
                          <div className="mb-2">
                            <strong>Total open Appointments:</strong> {metrics.openAppointments}
                          </div>
                          <div className="mb-2">
                            <strong>Amount due:</strong> {formatCurrency(metrics.amountDue)} {currencyCode}
                          </div>
                          <div className="mb-2">
                            <strong>Current membership balance:</strong> {formatCurrency(metrics.membershipBalance)} {currencyCode}
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-top">
                          <small className="text-muted">{getBusinessName()}</small>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Main Content Area */}
                  <Col md={8}>
                    {/* Upcoming Appointments */}
                    <Card className="mb-3 border-0 shadow-sm">
                      <CardBody>
                        <h6 className="mb-3">Upcoming Appointments</h6>
                        {upcomingAppointments.length === 0 ? (
                          <Alert color="info" className="mb-0">
                            There are no upcoming Appointments for this Guest
                          </Alert>
                        ) : (
                          <Table responsive size="sm">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Service</th>
                                <th>Time</th>
                                <th>Staff</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expandBookingsToServices(upcomingAppointments).map((serviceRow, index) => (
                                <tr key={`${serviceRow.booking_Id}-${serviceRow.detailIndex}-${serviceRow.resourceIndex}-${index}`}>
                                  <td>{formatDate(serviceRow.date)}</td>
                                  <td>{serviceRow.serviceName}</td>
                                  <td>{serviceRow.time}</td>
                                  <td>{serviceRow.staff}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>

                    {/* Past Appointments */}
                    <Card className="mb-3 border-0 shadow-sm">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">Past Appointments ({pastAppointments.length})</h6>
                          {pastAppointments.length > 3 && (
                            <Button color="link" size="sm">More</Button>
                          )}
                        </div>
                        {pastAppointments.length === 0 ? (
                          <Alert color="info" className="mb-0">No past appointments</Alert>
                        ) : (
                          <Table responsive size="sm">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Service</th>
                                <th>Cost</th>
                                <th>Staff</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expandBookingsToServices(pastAppointments.slice(0, 3)).map((serviceRow, index) => (
                                <tr key={`${serviceRow.booking_Id}-${serviceRow.detailIndex}-${serviceRow.resourceIndex}-${index}`}>
                                  <td>{formatDate(serviceRow.date)}</td>
                                  <td>{serviceRow.serviceName}</td>
                                  <td>{formatCurrency(serviceRow.serviceCost || 0)} {currencyCode}</td>
                                  <td>{serviceRow.staff}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>

                    {/* Packages */}
                    <Card className="mb-3 border-0 shadow-sm">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">Packages</h6>
                          {memberships.filter(m => {
                            const entityType = m.entity_Type || m.entityType;
                            return entityType === 2 || entityType === 3 ||
                              entityType === 'pac' || entityType === 'PAC' || entityType === 'Package' ||
                              entityType === 'sep' || entityType === 'SER' || entityType === 'PackageSeries';
                          }).length > 1 && (
                              <Button color="link" size="sm">More</Button>
                            )}
                        </div>
                        {memberships.filter(m => {
                          const entityType = m.entity_Type || m.entityType;
                          return entityType === 2 || entityType === 3 ||
                            entityType === 'pac' || entityType === 'PAC' || entityType === 'Package' ||
                            entityType === 'sep' || entityType === 'SER' || entityType === 'PackageSeries';
                        }).length === 0 ? (
                          <Alert color="info" className="mb-0">No packages found</Alert>
                        ) : (
                          <div>
                            {memberships.filter(m => {
                              const entityType = m.entity_Type || m.entityType;
                              return entityType === 2 || entityType === 3 ||
                                entityType === 'pac' || entityType === 'PAC' || entityType === 'Package' ||
                                entityType === 'sep' || entityType === 'SER' || entityType === 'PackageSeries';
                            }).slice(0, 1).map((membership, index) => {
                              // Calculate package details
                              const totalAmount = membership.installments?.reduce((sum, inst) => sum + (inst.amount || 0), 0) || 0;

                              // Get service and product names from membership details
                              const serviceNames = [];
                              const productNames = [];
                              if (membership.details && Array.isArray(membership.details)) {
                                membership.details.forEach(detail => {
                                  if (detail.serviceId) {
                                    const serviceName = getServiceNameById(detail.serviceId);
                                    if (serviceName && serviceName !== '-' && !serviceNames.includes(serviceName)) {
                                      serviceNames.push(serviceName);
                                    }
                                  }
                                  if (detail.productId) {
                                    const productName = getProductNameById(detail.productId);
                                    if (productName && productName !== '-' && !productNames.includes(productName)) {
                                      productNames.push(productName);
                                    }
                                  }
                                });
                              }

                              return (
                                <div key={membership.membershipId || index} className="border rounded p-3">
                                  <div className="d-flex justify-content-between mb-2">
                                    <strong>{getEntityName(membership)}</strong>
                                    <span>{formatCurrency(totalAmount)} {currencyCode}</span>
                                  </div>
                                  {serviceNames.length > 0 && (
                                    <div>
                                      <small><strong>Services:</strong> {serviceNames.join(', ')}</small>
                                    </div>
                                  )}
                                  {productNames.length > 0 && (
                                    <div>
                                      <small><strong>Products:</strong> {productNames.join(', ')}</small>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardBody>
                    </Card>

                  </Col>
                </Row>
              </TabPane>

              {/* Profile Tab */}
              <TabPane tabId="profile">
                <Row>
                  <Col md={6}>
                    <Card className="mb-3">
                      <CardBody>
                        <h6 className="mb-3 text-primary">Contact Information</h6>
                        <div className="mb-2">
                          <Mail size={16} className="me-2 text-muted" />
                          <strong>Email:</strong> {customerData.customer_Email || '-'}
                        </div>
                        <div className="mb-2">
                          <Phone size={16} className="me-2 text-muted" />
                          <strong>Contact:</strong> {customerData.customer_ContactNo || '-'}
                        </div>
                        {customerData.primaryContact_Name && (
                          <div className="mb-2">
                            <User size={16} className="me-2 text-muted" />
                            <strong>Primary Contact:</strong> {customerData.primaryContact_Name}
                            {customerData.primaryContact_Phone && ` (${customerData.primaryContact_Phone})`}
                          </div>
                        )}
                        <div className="mb-2">
                          <MapPin size={16} className="me-2 text-muted" />
                          <strong>Address (EN):</strong> {customerData.customer_AddressEn || '-'}
                        </div>
                        {customerData.customer_AddressAr && (
                          <div className="mb-2">
                            <MapPin size={16} className="me-2 text-muted" />
                            <strong>Address (AR):</strong> {customerData.customer_AddressAr}
                          </div>
                        )}
                        {customerData.customer_Country_Id && (() => {
                          const country = countries.find(c => c.country_Id === customerData.customer_Country_Id);
                          return country ? (
                            <div className="mb-2">
                              <MapPin size={16} className="me-2 text-muted" />
                              <strong>Country:</strong> {`${country.country_NameEn} - ${country.country_NameAr}`}
                            </div>
                          ) : null;
                        })()}
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="mb-3">
                      <CardBody>
                        <h6 className="mb-3 text-primary">Business Information</h6>
                        <div className="mb-2">
                          <CreditCard size={16} className="me-2 text-muted" />
                          <strong>Credit Limit:</strong> {formatCurrency(customerData.customer_CreditLimit)} {currencyCode}
                        </div>
                        {advanceBalance !== null && (
                          <div className="mb-2">
                            <Receipt size={16} className="me-2 text-success" />
                            <strong>Advance Balance:</strong> {formatCurrency(advanceBalance?.availableAdvance || advanceBalance?.AvailableAdvance || 0)} {currencyCode}
                          </div>
                        )}
                        {outstandingCredit !== null && (
                          <div className="mb-2">
                            <Receipt size={16} className="me-2 text-danger" />
                            <strong>Outstanding Credit:</strong> {formatCurrency(outstandingCredit?.totalOutstanding || outstandingCredit?.TotalOutstanding || 0)} {currencyCode}
                          </div>
                        )}
                        <div className="mb-2">
                          <Gift size={16} className="me-2 text-warning" />
                          <strong>Loyalty Balance:</strong> {loyaltyBalance} Points
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              {/* Notes Tab */}
              <TabPane tabId="notes">
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Customer Notes</h6>
                    <p>{customerData.customer_Notes || 'No notes available'}</p>
                  </CardBody>
                </Card>
              </TabPane>

              {/* SOAP Notes Tab */}
              <TabPane tabId="soapNotes">
                <Card>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Visit Notes (SOAP)</h6>
                    </div>
                    {soapNotesLoading ? (
                      <div className="text-center py-4">
                        <Spinner color="primary" size="sm" className="me-2" />
                        <span>Loading notes...</span>
                      </div>
                    ) : soapNotes.length === 0 ? (
                      <Alert color="info">No SOAP notes found for this customer.</Alert>
                    ) : (
                      <Table responsive striped hover className="align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Date</th>
                            <th>Staff</th>
                            <th>Details Summary</th>
                            <th className="text-center">View</th>
                          </tr>
                        </thead>
                        <tbody>
                          {soapNotes.map((soap) => (
                            <tr key={soap.soap_Id || soap.Soap_Id}>
                              <td>{formatDate(soap.date || soap.Date)}</td>
                              <td>{soap.staffName || soap.StaffName}</td>
                              <td>
                                <div className="text-truncate" style={{ maxWidth: '400px' }}>
                                  <small className="text-muted d-block">
                                    <strong>S:</strong> {soap.subjective || '-'}
                                  </small>
                                  <small className="text-muted d-block">
                                    <strong>P:</strong> {soap.plan || '-'}
                                  </small>
                                </div>
                              </td>
                              <td className="text-center">
                                <Button 
                                  color="soft-primary" 
                                  size="sm" 
                                  onClick={() => {
                                    setSelectedSoap(soap);
                                    setViewSoapModal(true);
                                  }}
                                >
                                  <i className="bx bx-show font-size-16"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </TabPane>

              {/* Appointment Tab */}
              <TabPane tabId="appointment">
                <Card>
                  <CardBody>
                    <h6 className="mb-3">All Appointments</h6>
                    {allBookings.length === 0 ? (
                      <Alert color="info">No appointments found</Alert>
                    ) : (
                      <Table responsive striped>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Date</th>
                            <th>Service</th>
                            <th>Time</th>
                            <th>Cost</th>
                            <th>Staff</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expandBookingsToServices(allBookings).map((serviceRow, index) => (
                            <tr key={`${serviceRow.booking_Id}-${serviceRow.detailIndex}-${serviceRow.resourceIndex}-${index}`}>
                              <td>{index + 1}</td>
                              <td>{formatDate(serviceRow.date)}</td>
                              <td>{serviceRow.serviceName}</td>
                              <td>{serviceRow.time}</td>
                              <td>{formatCurrency(serviceRow.serviceCost || 0)} {currencyCode}</td>
                              <td>{serviceRow.staff}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </TabPane>

              {/* Products Tab */}
              <TabPane tabId="products">
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Products Purchased</h6>
                    {(() => {
                      const invoiceProducts = getProductsFromInvoices();
                      console.log('📦 Invoice products to display:', invoiceProducts);
                      console.log('📦 Master products array:', products.length, products);

                      if (invoiceProducts.length === 0) {
                        return <Alert color="info">No products found in invoices</Alert>;
                      }

                      return (
                        <Table responsive striped>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Product Name</th>
                              <th>Total Quantity</th>
                              <th>Total Amount</th>
                              <th>Last Purchase</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoiceProducts.map((item, index) => {
                              // Get productId from the item (stored as number in productMap)
                              const productId = item.productId;

                              console.log('🔍 Looking up product ID:', productId, 'Type:', typeof productId);
                              console.log('🔍 Available master products:', products.length);

                              // Look up product name from master products array (state variable) - convert to number for comparison
                              const productIdNum = productId ? parseInt(productId, 10) : null;
                              const productData = products.find(p => {
                                const pId = parseInt(p.product_Id || p.productId || p.id || p.value || 0, 10);
                                const match = pId === productIdNum && pId > 0;
                                if (match) {
                                  console.log('✅ Match found! Product:', p.product_NameEn, 'ID:', pId);
                                }
                                return match;
                              });

                              console.log('✅ Found product data:', productData ? productData.product_NameEn : 'NOT FOUND');

                              const productName = productData
                                ? (productData.product_NameEn || productData.productName_En || productData.name || productData.label || `Product ${productId}`)
                                : (item.product?.product_NameEn || item.product?.productName_En || item.product?.name || item.product?.label || `Product ${productId || index}`);

                              console.log('📦 Product name resolved:', productName);

                              // Find last purchase date from invoices
                              let lastPurchaseDate = null;
                              for (const invoice of invoices) {
                                if (invoice.details && Array.isArray(invoice.details)) {
                                  const hasProduct = invoice.details.some(d => {
                                    const dProductId = parseInt(d.entityId || d.entity_Id || d.product_Id || d.productId || 0, 10);
                                    const entityType = d.entityType || d.entity_Type;
                                    return dProductId === productIdNum && dProductId > 0 && (entityType === 'pro' || entityType === 'product' || entityType === 'Product');
                                  });
                                  if (hasProduct) {
                                    lastPurchaseDate = invoice.Invoice_Date || invoice.invoice_Date;
                                    break; // Get the most recent (invoices are sorted)
                                  }
                                }
                              }

                              return (
                                <tr key={productId || index}>
                                  <td>{index + 1}</td>
                                  <td>{productName}</td>
                                  <td>{item.totalQuantity}</td>
                                  <td>{formatCurrency(item.totalAmount)} {currencyCode}</td>
                                  <td>{lastPurchaseDate ? formatDate(lastPurchaseDate) : '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      );
                    })()}
                  </CardBody>
                </Card>
              </TabPane>

              {/* Memberships Tab */}
              <TabPane tabId="memberships">
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Customer Memberships</h6>
                    {memberships.filter(m => {
                      const entityType = m.entity_Type || m.entityType;
                      return entityType === 3 ||
                        entityType === 'sep' || entityType === 'SER' || entityType === 'PackageSeries';
                    }).length === 0 ? (
                      <Alert color="info">No package series found</Alert>
                    ) : (
                      <Table responsive striped>
                        <thead>
                          <tr>
                            <th style={{ width: '30px' }}></th>
                            <th>#</th>
                            <th>Purchase Date</th>
                            <th>Entity Name</th>
                            <th>Branch</th>
                          </tr>
                        </thead>
                        <tbody>
                          {memberships.filter(m => {
                            const entityType = m.entity_Type || m.entityType;
                            return entityType === 3 ||
                              entityType === 'sep' || entityType === 'SER' || entityType === 'PackageSeries';
                          }).map((membership, index) => {
                            const membershipId = membership.membershipId || membership.MembershipId || index;
                            const isExpanded = expandedMemberships.has(membershipId);
                            const details = getMembershipDetails(membership);
                            const hasDetails = details.services.length > 0; // Only check services since products are commented out

                            return (
                              <React.Fragment key={membershipId}>
                                <tr>
                                  <td style={{ verticalAlign: 'middle' }}>
                                    {hasDetails && (
                                      <Button
                                        color="link"
                                        size="sm"
                                        onClick={() => toggleMembershipExpansion(membershipId)}
                                        style={{ padding: 0, minWidth: 'auto' }}
                                      >
                                        {isExpanded ? (
                                          <ChevronDown size={16} style={{ transform: 'rotate(180deg)' }} />
                                        ) : (
                                          <ChevronDown size={16} />
                                        )}
                                      </Button>
                                    )}
                                  </td>
                                  <td>{index + 1}</td>
                                  <td>{formatDate(membership.purchaseDate || membership.PurchaseDate)}</td>
                                  <td>{getEntityName(membership)}</td>
                                  <td>{branches.find(b => b.branch_Id === (membership.branchId || membership.BranchId))?.branch_NameEn || '-'}</td>
                                </tr>
                                {isExpanded && hasDetails && (
                                  <tr>
                                    <td colSpan="5" style={{ padding: 0, borderTop: 'none' }}>
                                      <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
                                        {/* Services Section */}
                                        {details.services.length > 0 && (
                                          <div className="mb-3">
                                            <h6 style={{ fontSize: '14px', fontWeight: 'bold', color: '#495057', marginBottom: '10px' }}>
                                              <i className="mdi mdi-spa me-2 text-primary"></i>
                                              Services
                                            </h6>
                                            <Table size="sm" bordered className="mb-0" style={{ backgroundColor: 'white' }}>
                                              <thead style={{ backgroundColor: '#e9ecef' }}>
                                                <tr>
                                                  <th style={{ fontSize: '12px', padding: '8px', color: '#212529' }}>Service Name</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Package</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Total Qty</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Available Qty</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Booked</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>In Progress</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Completed</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {details.services.map((service, serviceIndex) => (
                                                  <tr key={`service-${service.serviceId}-${serviceIndex}`}>
                                                    <td style={{ fontSize: '12px', padding: '8px' }}>
                                                      <strong>{service.serviceName}</strong>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {service.packageId ? (
                                                        <span style={{ color: '#212529', fontWeight: 'normal' }}>{service.packageName}</span>
                                                      ) : (
                                                        <span style={{ color: '#212529', fontWeight: 'normal' }}>Direct</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      <span style={{ color: '#212529', fontWeight: '500' }}>{service.totalQty}</span>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      <span style={{ color: '#212529', fontWeight: '500' }}>{service.availableQty}</span>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {service.bookedQty > 0 ? (
                                                        <span style={{ color: '#212529', fontWeight: '500' }}>{service.bookedQty}</span>
                                                      ) : (
                                                        <span style={{ color: '#212529' }}>-</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {service.inProgressQty > 0 ? (
                                                        <span style={{ color: '#212529', fontWeight: '500' }}>{service.inProgressQty}</span>
                                                      ) : (
                                                        <span style={{ color: '#212529' }}>-</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {service.completedQty > 0 ? (
                                                        <span style={{ color: '#212529', fontWeight: '500' }}>{service.completedQty}</span>
                                                      ) : (
                                                        <span style={{ color: '#212529' }}>-</span>
                                                      )}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          </div>
                                        )}

                                        {/* Products Section - Commented out as products are not selectable in packages/package series */}
                                        {/* Uncomment below section when products need to be displayed in membership tab */}
                                        {/* {details.products.length > 0 && (
                                          <div>
                                            <h6 style={{ fontSize: '14px', fontWeight: 'bold', color: '#495057', marginBottom: '10px' }}>
                                              <i className="mdi mdi-package-variant me-2 text-success"></i>
                                              Products
                                            </h6>
                                            <Table size="sm" bordered className="mb-0" style={{ backgroundColor: 'white' }}>
                                              <thead style={{ backgroundColor: '#e9ecef' }}>
                                                <tr>
                                                  <th style={{ fontSize: '12px', padding: '8px' }}>Product Name</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Package</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Total Qty</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Available Qty</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Completed</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>In Progress</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Pending</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {details.products.map((product, productIndex) => (
                                                  <tr key={`product-${product.productId}-${productIndex}`}>
                                                    <td style={{ fontSize: '12px', padding: '8px' }}>
                                                      <strong>{product.productName}</strong>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {product.packageId ? (
                                                        <span style={{ color: '#6c757d' }}>{product.packageName}</span>
                                                      ) : (
                                                        <span style={{ color: '#6c757d' }}>Direct</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      <Badge color="secondary" style={{ color: '#fff' }}>{product.totalQty}</Badge>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      <Badge color="info" style={{ color: '#fff' }}>{product.availableQty}</Badge>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {product.completedQty > 0 ? (
                                                        <Badge color="success" style={{ color: '#fff' }}>{product.completedQty}</Badge>
                                                      ) : (
                                                        <span style={{ color: '#6c757d' }}>-</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {product.inProgressQty > 0 ? (
                                                        <Badge color="warning" style={{ color: '#212529' }}>{product.inProgressQty}</Badge>
                                                      ) : (
                                                        <span style={{ color: '#6c757d' }}>-</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {product.pendingQty > 0 ? (
                                                        <Badge color="secondary" style={{ color: '#fff' }}>{product.pendingQty}</Badge>
                                                      ) : (
                                                        <span style={{ color: '#6c757d' }}>-</span>
                                                      )}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          </div>
                                        )} */}

                                        {!hasDetails && (
                                          <div className="text-center text-muted py-2">
                                            <small>No service details available</small>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </TabPane>

              {/* Packages Tab */}
              <TabPane tabId="packages">
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Customer Packages</h6>
                    {memberships.filter(m => {
                      const entityType = m.entity_Type || m.entityType;
                      return entityType === 2 ||
                        entityType === 'pac' || entityType === 'PAC' || entityType === 'Package';
                    }).length === 0 ? (
                      <Alert color="info">No packages found</Alert>
                    ) : (
                      <Table responsive striped>
                        <thead>
                          <tr>
                            <th style={{ width: '30px' }}></th>
                            <th>#</th>
                            <th>Purchase Date</th>
                            <th>Entity Name</th>
                            <th>Branch</th>
                          </tr>
                        </thead>
                        <tbody>
                          {memberships.filter(m => {
                            const entityType = m.entity_Type || m.entityType;
                            return entityType === 2 ||
                              entityType === 'pac' || entityType === 'PAC' || entityType === 'Package';
                          }).map((membership, index) => {
                            const membershipId = membership.membershipId || membership.MembershipId || index;
                            const isExpanded = expandedMemberships.has(membershipId);
                            const details = getMembershipDetails(membership);
                            const hasDetails = details.services.length > 0; // Only check services since products are commented out

                            return (
                              <React.Fragment key={membershipId}>
                                <tr>
                                  <td style={{ verticalAlign: 'middle' }}>
                                    {hasDetails && (
                                      <Button
                                        color="link"
                                        size="sm"
                                        onClick={() => toggleMembershipExpansion(membershipId)}
                                        style={{ padding: 0, minWidth: 'auto' }}
                                      >
                                        {isExpanded ? (
                                          <ChevronDown size={16} style={{ transform: 'rotate(180deg)' }} />
                                        ) : (
                                          <ChevronDown size={16} />
                                        )}
                                      </Button>
                                    )}
                                  </td>
                                  <td>{index + 1}</td>
                                  <td>{formatDate(membership.purchaseDate || membership.PurchaseDate)}</td>
                                  <td>{getEntityName(membership)}</td>
                                  <td>{branches.find(b => b.branch_Id === (membership.branchId || membership.BranchId))?.branch_NameEn || '-'}</td>
                                </tr>
                                {isExpanded && hasDetails && (
                                  <tr>
                                    <td colSpan="5" style={{ padding: 0, borderTop: 'none' }}>
                                      <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
                                        {/* Services Section */}
                                        {details.services.length > 0 && (
                                          <div className="mb-3">
                                            <h6 style={{ fontSize: '14px', fontWeight: 'bold', color: '#495057', marginBottom: '10px' }}>
                                              <i className="mdi mdi-spa me-2 text-primary"></i>
                                              Services
                                            </h6>
                                            <Table size="sm" bordered className="mb-0" style={{ backgroundColor: 'white' }}>
                                              <thead style={{ backgroundColor: '#e9ecef' }}>
                                                <tr>
                                                  <th style={{ fontSize: '12px', padding: '8px', color: '#212529' }}>Service Name</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Package</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Total Qty</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Available Qty</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Booked</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>In Progress</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>Completed</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {details.services.map((service, serviceIndex) => (
                                                  <tr key={`service-${service.serviceId}-${serviceIndex}`}>
                                                    <td style={{ fontSize: '12px', padding: '8px' }}>
                                                      <strong>{service.serviceName}</strong>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {service.packageId ? (
                                                        <span style={{ color: '#212529', fontWeight: 'normal' }}>{service.packageName}</span>
                                                      ) : (
                                                        <span style={{ color: '#212529', fontWeight: 'normal' }}>Direct</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      <span style={{ color: '#212529', fontWeight: '500' }}>{service.totalQty}</span>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      <span style={{ color: '#212529', fontWeight: '500' }}>{service.availableQty}</span>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {service.bookedQty > 0 ? (
                                                        <span style={{ color: '#212529', fontWeight: '500' }}>{service.bookedQty}</span>
                                                      ) : (
                                                        <span style={{ color: '#212529' }}>-</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {service.inProgressQty > 0 ? (
                                                        <span style={{ color: '#212529', fontWeight: '500' }}>{service.inProgressQty}</span>
                                                      ) : (
                                                        <span style={{ color: '#212529' }}>-</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {service.completedQty > 0 ? (
                                                        <span style={{ color: '#212529', fontWeight: '500' }}>{service.completedQty}</span>
                                                      ) : (
                                                        <span style={{ color: '#212529' }}>-</span>
                                                      )}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          </div>
                                        )}

                                        {/* Products Section - Commented out as products are not selectable in packages/package series */}
                                        {/* Uncomment below section when products need to be displayed in membership tab */}
                                        {/* {details.products.length > 0 && (
                                          <div>
                                            <h6 style={{ fontSize: '14px', fontWeight: 'bold', color: '#495057', marginBottom: '10px' }}>
                                              <i className="mdi mdi-package-variant me-2 text-success"></i>
                                              Products
                                            </h6>
                                            <Table size="sm" bordered className="mb-0" style={{ backgroundColor: 'white' }}>
                                              <thead style={{ backgroundColor: '#e9ecef' }}>
                                                <tr>
                                                  <th style={{ fontSize: '12px', padding: '8px' }}>Product Name</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Package</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Total Qty</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Available Qty</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Completed</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>In Progress</th>
                                                  <th style={{ fontSize: '12px', padding: '8px', textAlign: 'center' }}>Pending</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {details.products.map((product, productIndex) => (
                                                  <tr key={`product-${product.productId}-${productIndex}`}>
                                                    <td style={{ fontSize: '12px', padding: '8px' }}>
                                                      <strong>{product.productName}</strong>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {product.packageId ? (
                                                        <span style={{ color: '#6c757d' }}>{product.packageName}</span>
                                                      ) : (
                                                        <span style={{ color: '#6c757d' }}>Direct</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      <Badge color="secondary" style={{ color: '#fff' }}>{product.totalQty}</Badge>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      <Badge color="info" style={{ color: '#fff' }}>{product.availableQty}</Badge>
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {product.completedQty > 0 ? (
                                                        <Badge color="success" style={{ color: '#fff' }}>{product.completedQty}</Badge>
                                                      ) : (
                                                        <span style={{ color: '#6c757d' }}>-</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {product.inProgressQty > 0 ? (
                                                        <Badge color="warning" style={{ color: '#212529' }}>{product.inProgressQty}</Badge>
                                                      ) : (
                                                        <span style={{ color: '#6c757d' }}>-</span>
                                                      )}
                                                    </td>
                                                    <td style={{ fontSize: '12px', padding: '8px', textAlign: 'center', color: '#212529' }}>
                                                      {product.pendingQty > 0 ? (
                                                        <Badge color="secondary" style={{ color: '#fff' }}>{product.pendingQty}</Badge>
                                                      ) : (
                                                        <span style={{ color: '#6c757d' }}>-</span>
                                                      )}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          </div>
                                        )} */}

                                        {!hasDetails && (
                                          <div className="text-center text-muted py-2">
                                            <small>No service details available</small>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </TabPane>

              {/* Unbilled Services Tab */}
              <TabPane tabId="unbilled">
                {unbilledLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading unbilled services...</p>
                  </div>
                ) : (
                  <div style={{ fontSize: '0.875rem' }}>
                    {/* Section 1: Membership Installments */}
                    {unbilledMemberships.length > 0 && (
                      <div className="mb-4">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <h5 className="text-primary mb-0" style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                            <i className="fas fa-credit-card me-2"></i>Membership Installments
                          </h5>
                          <div className="form-check">
                            <Input
                              className="form-check-input"
                              type="checkbox"
                              id="select-all-installments"
                              checked={selectAllInstallments}
                              onChange={(e) => handleSelectAllInstallments(e.target.checked)}
                            />
                            <Label className="form-check-label" htmlFor="select-all-installments" style={{ fontSize: '0.875rem', cursor: 'pointer' }}>
                              Select All
                            </Label>
                          </div>
                        </div>
                        <div style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '0.875rem' }}>
                          {unbilledMemberships.map((membership) => (
                            <div key={membership.membershipId} className="mb-3">
                              <div className="border rounded" style={{ padding: '0.75rem' }}>
                                <h6 className="text-primary mb-2" style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                                  <i className="fas fa-credit-card me-2"></i>
                                  {isPackageType(membership.entity_Type) ? 'Package' : 'Package Series'} Membership
                                </h6>
                                <div className="mb-2" style={{ fontSize: '0.875rem' }}>
                                  {isPackageType(membership.entity_Type) ? 'Package' : 'Series'}: {entityNames[membership.entity_Id] || `${isPackageType(membership.entity_Type) ? 'Package' : 'Series'} ${membership.entity_Id}`} |
                                  Purchase Date: {new Date(membership.purchaseDate).toLocaleDateString('en-GB')}
                                </div>

                                {membership.unpaidInstallments.length > 0 && (
                                  <div>
                                    <h6 className="text-secondary mb-2" style={{ fontSize: '0.875rem' }}>Available Installments:</h6>
                                    {membership.unpaidInstallments.map((installment) => {
                                      const sequentialNumber = membership.minInstallmentId !== null
                                        ? installment.installmentId - membership.minInstallmentId + 1
                                        : installment.installmentId;
                                      const installmentName = installmentNames[installment.installmentId] || `Installment ${sequentialNumber}`;
                                      const billedAmount = installment.billedAmount || installment.paidAmount || 0;
                                      const remainingAmount = Math.round((installment.amount - billedAmount) * 100) / 100;
                                      const isSelected = selectedInstallments[installment.installmentId]?.checked || false;

                                      return (
                                        <div key={installment.installmentId} className="d-flex align-items-center justify-content-between mb-1 p-2 border rounded" style={{ fontSize: '0.875rem', backgroundColor: isSelected ? '#e7f3ff' : '#fff' }}>
                                          <div className="form-check" style={{ flex: '1' }}>
                                            <Input
                                              className="form-check-input"
                                              type="checkbox"
                                              id={`installment-${installment.installmentId}`}
                                              checked={isSelected}
                                              onChange={() => handleInstallmentToggle(installment.installmentId, membership, installment)}
                                            />
                                            <Label className="form-check-label" htmlFor={`installment-${installment.installmentId}`} style={{ cursor: 'pointer' }}>
                                              <strong>{installmentName}</strong>
                                            </Label>
                                          </div>
                                          <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                                            Amount: {installment.amount.toFixed(2)} |
                                            Billed: {billedAmount.toFixed(2)} |
                                            Remaining: {remainingAmount.toFixed(2)}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Section 2: Unbilled Services from Bookings */}
                    {unbilledServices.length > 0 && (
                      <div className={unbilledMemberships.length > 0 ? "mt-4 pt-4 border-top" : ""}>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <h5 className="text-info mb-0" style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                            <i className="fas fa-list-check me-2"></i>Unbilled Services from Bookings
                          </h5>
                          <div className="form-check">
                            <Input
                              className="form-check-input"
                              type="checkbox"
                              id="select-all-services"
                              checked={selectAllServices}
                              onChange={(e) => handleSelectAllServices(e.target.checked)}
                            />
                            <Label className="form-check-label" htmlFor="select-all-services" style={{ fontSize: '0.875rem', cursor: 'pointer' }}>
                              Select All
                            </Label>
                          </div>
                        </div>
                        <div style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '0.875rem' }}>
                          {unbilledServices.map((service) => {
                            const bookingResourceDetailId = service.bookingResourceDetail_Id || service.BookingResourceDetail_Id;
                            const calculatedAmount = service.calculatedAmount || service.CalculatedAmount || 0;
                            const serviceNetAmount = service.serviceNetAmount || service.ServiceNetAmount || 0;
                            const packageNetAmount = service.packageNetAmount || service.PackageNetAmount || 0;
                            const serviceName = service.spaServiceName_En || service.SpaServiceName_En || null;
                            const packageName = service.packageName_En || service.PackageName_En || null;
                            const serviceId = service.service_Id || service.Service_Id;
                            const bookingId = service.booking_Id || service.Booking_Id;
                            const bookingDate = service.booking_Date || service.Booking_Date;
                            const employeeName = service.employeeName_En || service.EmployeeName_En;
                            const roomName = service.roomName_En || service.RoomName_En;
                            const fromTime = service.from_Time || service.From_Time;
                            const toTime = service.to_Time || service.To_Time;
                            const daysSinceCompletion = service.daysSinceCompletion || service.DaysSinceCompletion;
                            const isSelected = selectedUnbilledServices[bookingResourceDetailId]?.checked || false;

                            return (
                              <div key={bookingResourceDetailId} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded" style={{ fontSize: '0.875rem', backgroundColor: isSelected ? '#e7f3ff' : '#fff' }}>
                                <div className="form-check" style={{ flex: '1' }}>
                                  <Input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`service-${bookingResourceDetailId}`}
                                    checked={isSelected}
                                    onChange={() => handleServiceToggle(service)}
                                  />
                                  <Label className="form-check-label" htmlFor={`service-${bookingResourceDetailId}`} style={{ cursor: 'pointer' }}>
                                    <div>
                                      <strong>{serviceName || packageName || `Service ${serviceId}`}</strong>
                                      {packageName && serviceName && (
                                        <span className="text-muted ms-2">(Package: {packageName})</span>
                                      )}
                                    </div>
                                    <div className="text-muted small mt-1">
                                      Booking: #{bookingId} |
                                      Date: {bookingDate ? new Date(bookingDate).toLocaleDateString('en-GB') : 'N/A'} |
                                      {employeeName && `Employee: ${employeeName} | `}
                                      {roomName && `Room: ${roomName} | `}
                                      {fromTime && toTime && (
                                        `Time: ${new Date(fromTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - ${new Date(toTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                                      )}
                                    </div>
                                  </Label>
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.875rem', textAlign: 'right' }}>
                                  <div>Amount: {(calculatedAmount || packageNetAmount || serviceNetAmount || 0).toFixed(2)}</div>
                                  {daysSinceCompletion !== null && daysSinceCompletion !== undefined && (
                                    <div className="small">Completed {daysSinceCompletion} day(s) ago</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-2 text-muted small" style={{ fontSize: '0.75rem' }}>
                          <i className="mdi mdi-information-outline me-1"></i>
                          These are completed services from bookings that have not been billed yet.
                        </div>
                      </div>
                    )}

                    {/* Show message if no items available */}
                    {unbilledMemberships.length === 0 && unbilledServices.length === 0 && (
                      <Alert color="info" className="mt-3">
                        No unbilled services or installments found for this customer.
                      </Alert>
                    )}

                    {/* Bill Selected Button */}
                    {(unbilledMemberships.length > 0 || unbilledServices.length > 0) && (
                      <div className="mt-4 pt-3 border-top">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                            Selected: {
                              Object.values(selectedInstallments).filter(item => item.checked).length
                            } installment(s), {
                              Object.values(selectedUnbilledServices).filter(item => item.checked).length
                            } service(s)
                          </div>
                          <Button
                            color="primary"
                            onClick={handleBillSelected}
                            disabled={
                              (Object.values(selectedInstallments).filter(item => item && item.checked).length === 0) &&
                              (Object.values(selectedUnbilledServices).filter(item => item && item.checked).length === 0)
                            }
                            style={{ fontSize: '0.875rem' }}
                          >
                            <i className="fas fa-receipt me-2"></i>
                            Bill Selected
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </TabPane>

              {/* Payment History Tab */}
              <TabPane tabId="paymentHistory">
                {paymentHistoryLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading payment history...</p>
                  </div>
                ) : (
                  <div style={{ fontSize: '0.875rem' }}>
                    {/* Section 1: Installment Payments */}
                    {memberships.length > 0 && (() => {
                      // Get all installments with payment info
                      const installmentPayments = [];
                      memberships.forEach(membership => {
                        if (membership.installments && membership.installments.length > 0) {
                          membership.installments.forEach(installment => {
                            const billedAmount = installment.billedAmount || installment.paidAmount || 0;
                            if (billedAmount > 0 && installment.invoice_Id) {
                              // Find the invoice to get the date
                              const invoice = invoices.find(inv =>
                                (inv.Invoice_Id || inv.invoice_Id) === installment.invoice_Id
                              );

                              const entityName = getEntityName(membership);
                              installmentPayments.push({
                                installmentId: installment.m_InstallmentId || installment.M_InstallmentId,
                                membershipId: membership.membershipId || membership.MembershipId,
                                entityName: entityName,
                                installmentNumber: installment.installmentId || installment.InstallmentId,
                                amount: billedAmount,
                                invoiceId: installment.invoice_Id || installment.Invoice_Id,
                                invoiceNo: invoice ? (invoice.Invoice_No || invoice.invoice_No) : null,
                                paymentDate: invoice ? (invoice.Invoice_Date || invoice.invoice_Date) : null
                              });
                            }
                          });
                        }
                      });

                      // Sort by payment date (most recent first)
                      installmentPayments.sort((a, b) => {
                        if (!a.paymentDate && !b.paymentDate) return 0;
                        if (!a.paymentDate) return 1;
                        if (!b.paymentDate) return -1;
                        return new Date(b.paymentDate) - new Date(a.paymentDate);
                      });

                      return installmentPayments.length > 0 ? (
                        <div className="mb-4">
                          <h5 className="text-primary mb-3" style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                            <i className="fas fa-credit-card me-2"></i>Installment Payments
                          </h5>
                          <div style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '0.875rem' }}>
                            {installmentPayments.map((payment) => (
                              <div key={payment.installmentId} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded" style={{ fontSize: '0.875rem', backgroundColor: '#fff' }}>
                                <div style={{ flex: '1' }}>
                                  <div>
                                    <strong>{payment.entityName}</strong>
                                    <span className="text-muted ms-2">- Installment #{payment.installmentNumber}</span>
                                  </div>
                                  <div className="text-muted small mt-1">
                                    {payment.invoiceNo && `Invoice: ${payment.invoiceNo} | `}
                                    {payment.paymentDate && `Date: ${new Date(payment.paymentDate).toLocaleDateString('en-GB')}`}
                                  </div>
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.875rem', textAlign: 'right' }}>
                                  <div>Amount: {payment.amount.toFixed(2)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* Section 2: Credit Invoices */}
                    {creditInvoices.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-primary mb-3" style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                          <i className="fas fa-file-invoice-dollar me-2"></i>Credit Invoices
                        </h5>
                        <div style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '0.875rem' }}>
                          {creditInvoices
                            .sort((a, b) => {
                              const dateA = a.Invoice_Date || a.invoice_Date;
                              const dateB = b.Invoice_Date || b.invoice_Date;
                              if (!dateA && !dateB) return 0;
                              if (!dateA) return 1;
                              if (!dateB) return -1;
                              return new Date(dateB) - new Date(dateA);
                            })
                            .map((invoice) => {
                              const invoiceId = invoice.Invoice_Id || invoice.invoice_Id;
                              const invoiceNo = invoice.Invoice_No || invoice.invoice_No;
                              const invoiceDate = invoice.Invoice_Date || invoice.invoice_Date;
                              const netAmount = invoice.Invoice_NetAmount || invoice.invoice_NetAmount || 0;
                              const paidAmount = invoice.PaidAmount || invoice.paidAmount || 0;
                              // Use OutstandingAmount from API (calculated from ReceiptDetails)
                              const outstandingAmount = invoice.OutstandingAmount || invoice.outstandingAmount || (netAmount - paidAmount);

                              return (
                                <div key={invoiceId} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded" style={{ fontSize: '0.875rem', backgroundColor: '#fff' }}>
                                  <div style={{ flex: '1' }}>
                                    <div>
                                      <strong>Invoice: {invoiceNo || `#${invoiceId}`}</strong>
                                    </div>
                                    <div className="text-muted small mt-1">
                                      Date: {invoiceDate ? new Date(invoiceDate).toLocaleDateString('en-GB') : 'N/A'}
                                    </div>
                                  </div>
                                  <div className="text-muted" style={{ fontSize: '0.875rem', textAlign: 'right' }}>
                                    <div>Amount: {netAmount.toFixed(2)}</div>
                                    {paidAmount > 0 && (
                                      <div className="small text-success">Paid: {paidAmount.toFixed(2)}</div>
                                    )}
                                    {outstandingAmount > 0 && (
                                      <div className="small text-warning">Outstanding: {outstandingAmount.toFixed(2)}</div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {/* Section 3: Cash Invoices */}
                    {(() => {
                      const cashInvoices = invoices.filter(inv => {
                        const invoiceType = inv.Invoice_Type || inv.invoice_Type;
                        return invoiceType === 'ca' || invoiceType === 'CA';
                      });

                      return cashInvoices.length > 0 ? (
                        <div className="mb-4">
                          <h5 className="text-primary mb-3" style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                            <i className="fas fa-money-bill-wave me-2"></i>Cash Invoices
                          </h5>
                          <div style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '0.875rem' }}>
                            {cashInvoices
                              .sort((a, b) => {
                                const dateA = a.Invoice_Date || a.invoice_Date;
                                const dateB = b.Invoice_Date || b.invoice_Date;
                                if (!dateA && !dateB) return 0;
                                if (!dateA) return 1;
                                if (!dateB) return -1;
                                return new Date(dateB) - new Date(dateA);
                              })
                              .map((invoice) => {
                                const invoiceId = invoice.Invoice_Id || invoice.invoice_Id;
                                const invoiceNo = invoice.Invoice_No || invoice.invoice_No;
                                const invoiceDate = invoice.Invoice_Date || invoice.invoice_Date;
                                const netAmount = invoice.Invoice_NetAmount || invoice.invoice_NetAmount || 0;
                                const cashAmount = invoice.CashAmount || invoice.cashAmount || 0;
                                const cashlessAmount = invoice.CashlessAmount || invoice.cashlessAmount || 0;
                                const paymentMethod = invoice.PaymentMethod || invoice.paymentMethod || '';

                                // Get payment method label
                                const getPaymentMethodLabel = (method) => {
                                  const methods = {
                                    'ca': 'Cash',
                                    'cc': 'Credit Card',
                                    'ba': 'Bank Transfer',
                                    'on': 'Online',
                                    'ta': 'Tabee',
                                    'tm': 'Tamara'
                                  };
                                  return methods[method?.toLowerCase()] || method || 'Cash';
                                };

                                return (
                                  <div key={invoiceId} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded" style={{ fontSize: '0.875rem', backgroundColor: '#fff' }}>
                                    <div style={{ flex: '1' }}>
                                      <div>
                                        <strong>Invoice: {invoiceNo || `#${invoiceId}`}</strong>
                                      </div>
                                      <div className="text-muted small mt-1">
                                        Date: {invoiceDate ? new Date(invoiceDate).toLocaleDateString('en-GB') : 'N/A'} |
                                        Payment: {getPaymentMethodLabel(paymentMethod)}
                                      </div>
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '0.875rem', textAlign: 'right' }}>
                                      <div>Amount: {netAmount.toFixed(2)}</div>
                                      {cashAmount > 0 && (
                                        <div className="small">Cash: {cashAmount.toFixed(2)}</div>
                                      )}
                                      {cashlessAmount > 0 && (
                                        <div className="small">Cashless: {cashlessAmount.toFixed(2)}</div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* Section 4: Receipts */}
                    {receipts.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-primary mb-3" style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                          <i className="fas fa-receipt me-2"></i>Receipts
                        </h5>
                        <div style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '0.875rem' }}>
                          {receipts
                            .sort((a, b) => {
                              const dateA = a.ReceiptDate || a.receiptDate;
                              const dateB = b.ReceiptDate || b.receiptDate;
                              if (!dateA && !dateB) return 0;
                              if (!dateA) return 1;
                              if (!dateB) return -1;
                              return new Date(dateB) - new Date(dateA);
                            })
                            .map((receipt) => {
                              const receiptId = receipt.ReceiptId || receipt.receiptId;
                              const receiptNo = receipt.ReceiptNumber || receipt.receiptNumber;
                              const receiptDate = receipt.ReceiptDate || receipt.receiptDate;
                              const amount = receipt.Amount || receipt.amount || 0;
                              const paymentType = receipt.PaymentType || receipt.paymentType || '';
                              const isAdvance = receipt.Is_Advance || receipt.is_Advance || false;

                              // Get payment type label
                              const getPaymentTypeLabel = (type) => {
                                const types = {
                                  'ca': 'Cash',
                                  'cc': 'Credit Card',
                                  'ba': 'Bank Transfer',
                                  'on': 'Online',
                                  'ta': 'Tabee',
                                  'tm': 'Tamara'
                                };
                                return types[type?.toLowerCase()] || type || 'Cash';
                              };

                              return (
                                <div key={receiptId} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded" style={{ fontSize: '0.875rem', backgroundColor: '#fff' }}>
                                  <div style={{ flex: '1' }}>
                                    <div>
                                      <strong>Receipt: {receiptNo || `#${receiptId}`}</strong>
                                      {isAdvance && (
                                        <Badge color="info" className="ms-2" style={{ fontSize: '0.7rem' }}>Advance</Badge>
                                      )}
                                    </div>
                                    <div className="text-muted small mt-1">
                                      Date: {receiptDate ? new Date(receiptDate).toLocaleDateString('en-GB') : 'N/A'} |
                                      Payment: {getPaymentTypeLabel(paymentType)}
                                    </div>
                                  </div>
                                  <div className="text-muted" style={{ fontSize: '0.875rem', textAlign: 'right' }}>
                                    <div>Amount: {amount.toFixed(2)}</div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {/* Show message if no payment history available */}
                    {(() => {
                      const hasInstallmentPayments = memberships.some(m =>
                        m.installments && m.installments.some(i => (i.billedAmount || i.paidAmount || 0) > 0 && i.invoice_Id)
                      );
                      const hasCashInvoices = invoices.some(inv => {
                        const invoiceType = inv.Invoice_Type || inv.invoice_Type;
                        return invoiceType === 'ca' || invoiceType === 'CA';
                      });

                      if (!hasInstallmentPayments && creditInvoices.length === 0 && !hasCashInvoices && receipts.length === 0) {
                        return (
                          <Alert color="info" className="mt-3">
                            No payment history found for this customer.
                          </Alert>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
              </TabPane>

              {/* WhatsApp Messages Tab */}
              <TabPane tabId="whatsappMessages">
                <Card className="border-0 shadow-sm">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0 text-primary">WhatsApp History</h5>
                      {whatsappMessageCount !== null && whatsappMessageCount > 0 && (
                        <Badge color="success" pill style={{ backgroundColor: '#25D366' }}>
                          {whatsappMessageCount} Messages
                        </Badge>
                      )}
                    </div>

                    {whatsappCountLoading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Checking message count...</p>
                      </div>
                    ) : whatsappMessageCount === 0 || whatsappMessageCount === null ? (
                      <div className="text-center py-5 border rounded bg-light">
                        <i className="mdi mdi-whatsapp text-muted" style={{ fontSize: '48px' }}></i>
                        <p className="mt-2 mb-0">No messages received from this customer</p>
                      </div>
                    ) : (
                      <div>
                        {!showMessages ? (
                          <div className="text-center py-5 border rounded bg-light">
                            <i className="mdi mdi-whatsapp text-success mb-3" style={{ fontSize: '48px', color: '#25D366' }}></i>
                            <h5>{whatsappMessageCount} Messages Available</h5>
                            <p className="text-muted mb-4">Messages are only loaded on request to save bandwidth.</p>
                            <Button 
                              color="success" 
                              size="lg"
                              onClick={loadWhatsAppMessages}
                              disabled={whatsappMessagesLoading}
                              style={{ backgroundColor: '#25D366', borderColor: '#25D366', borderRadius: '30px', padding: '10px 30px' }}
                            >
                              {whatsappMessagesLoading ? (
                                <><Spinner size="sm" className="me-2" /> Loading...</>
                              ) : (
                                `Show ${whatsappMessageCount} Messages`
                              )}
                            </Button>
                          </div>
                        ) : (
                          <div className="whatsapp-chat-container">
                            {whatsappMessages.length === 0 ? (
                              <p className="text-center text-muted py-3">No messages to display</p>
                            ) : (
                              <div className="table-responsive">
                                <Table hover className="align-middle">
                                  <thead className="table-light">
                                    <tr>
                                      <th style={{ width: '180px' }}>Date & Time</th>
                                      <th>Message</th>
                                      <th style={{ width: '100px' }}>Type</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {whatsappMessages.map((msg, idx) => (
                                      <tr key={msg.Id || idx}>
                                        <td className="text-nowrap" style={{ fontSize: '13px' }}>
                                          <div className="fw-bold">{new Date(msg.ReceivedAt).toLocaleDateString('en-GB')}</div>
                                          <div className="text-muted">{new Date(msg.ReceivedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                                        </td>
                                        <td style={{ minWidth: '200px', whiteSpace: 'pre-wrap' }}>
                                          {msg.MessageBody}
                                        </td>
                                        <td>
                                          <Badge color="light" className="text-dark border text-capitalize">
                                            {msg.MessageType || 'text'}
                                          </Badge>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </TabPane>
            </TabContent>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>

      </Modal>

      {/* View SOAP Details Modal */}
      <Modal 
        isOpen={viewSoapModal} 
        toggle={() => setViewSoapModal(false)} 
        size="lg" 
        centered 
        className="soap-details-modal"
        style={{ zIndex: 1060 }} // Higher than the profile modal
      >
        <div className="modal-header-gradient p-3 d-flex justify-content-between align-items-center">
          <h5 className="modal-title text-white">Visit Note Details</h5>
          <button type="button" className="btn-close btn-close-white" onClick={() => setViewSoapModal(false)}></button>
        </div>
        <ModalBody className="p-4 bg-light bg-opacity-50">
          {selectedSoap && (
            <>
              {/* Header Info Section */}
              <div className="bg-white p-4 rounded-4 shadow-sm mb-4">
                <Row className="align-items-center">
                  <Col md={5} className="d-flex align-items-center">
                    <div className="avatar-md me-3">
                      <span className="avatar-title rounded-circle bg-soft-primary text-primary font-size-24">
                        <i className="bx bx-user"></i>
                      </span>
                    </div>
                    <div>
                      <label className="text-muted font-size-11 fw-bold text-uppercase mb-1 d-block">Client</label>
                      <h5 className="mb-0 text-primary fw-bold">{selectedSoap.customerName || customerData?.customer_NameEn}</h5>
                      <span className="text-muted font-size-13">{selectedSoap.customerContact || customerData?.customer_ContactNo}</span>
                    </div>
                  </Col>
                  <Col md={1} className="d-none d-md-block">
                    <div className="info-divider"></div>
                  </Col>
                  <Col md={3} className="text-center text-md-start">
                    <label className="text-muted font-size-11 fw-bold text-uppercase mb-1 d-block">Date</label>
                    <div className="d-flex align-items-center justify-content-center justify-content-md-start">
                      <i className="bx bx-calendar-event font-size-18 text-primary me-2"></i>
                      <h6 className="mb-0 fw-semibold">{formatDate(selectedSoap.date || selectedSoap.Date)}</h6>
                    </div>
                  </Col>
                  <Col md={1} className="d-none d-md-block">
                    <div className="info-divider"></div>
                  </Col>
                  <Col md={2}>
                    <label className="text-muted font-size-11 fw-bold text-uppercase mb-1 d-block">Therapist</label>
                    <h6 className="mb-0 text-primary fw-bold">{selectedSoap.staffName || selectedSoap.StaffName || "N/A"}</h6>
                  </Col>
                </Row>
              </div>

              {/* SOAP Content Cards */}
              <div className="soap-cards-container">
                {[
                  { key: 'S', label: 'Subjective', content: selectedSoap.subjective || selectedSoap.Subjective, color: 'primary' },
                  { key: 'O', label: 'Objective', content: selectedSoap.objective || selectedSoap.Objective, color: 'info' },
                  { key: 'A', label: 'Assessment', content: selectedSoap.assessment || selectedSoap.Assessment, color: 'success' },
                  { key: 'P', label: 'Plan', content: selectedSoap.plan || selectedSoap.Plan, color: 'warning' }
                ].map((s, idx) => (
                  <Card key={idx} className="border-0 shadow-sm mb-3 rounded-4 overflow-hidden">
                    <CardBody className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className={`soap-letter-icon bg-${s.color} text-white me-3`}>
                          {s.key}
                        </div>
                        <h5 className="mb-0 fw-bold">{s.label}</h5>
                      </div>
                      <div className="border-top pt-3">
                        <p className="text-muted mb-0 lh-base" style={{ fontSize: '0.95rem' }}>
                          {s.content || "No notes provided for this section."}
                        </p>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter className="border-0 bg-light bg-opacity-50 pb-4 justify-content-center">
          <Button 
            color="light" 
            onClick={() => setViewSoapModal(false)} 
            className="rounded-pill px-5 py-2 fw-semibold shadow-sm border"
            style={{ minWidth: '160px' }}
          >
            Close View
          </Button>
        </ModalFooter>
      </Modal>

      <style>{`
        .soap-details-modal .modal-content {
          border-radius: 20px;
          border: none;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }
        .modal-header-gradient {
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
        }
        .info-divider {
          width: 1px;
          height: 40px;
          background-color: #e9ecef;
          margin: 0 auto;
        }
        .soap-letter-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 16px;
          box-shadow: 0 3px 6px rgba(0,0,0,0.1);
        }
        .bg-soft-primary {
          background-color: rgba(59, 130, 246, 0.1) !important;
        }
        .lh-base { line-height: 1.6; }
        .soap-details-modal .modal-title { color: white !important; }
      `}</style>
    </React.Fragment>
  );
};

export default CustomerProfileModal;